<template>
  <!--Loading Form Details-->
  <div v-if="isLoadingInit" class="d-flex flex-column align-center justify-center">
    <VProgressCircular indeterminate color="primary" class="my-8" />
  </div>
  <!--Form Status Pending-->
  <VCard v-else-if="isFormPending" max-width="800px" elevation="10">
    <VContainer>
      <!-- Header -->
      <div class="form-header mb-8">
        <h1 class="form-title">Short Form</h1>
        <div class="title-bar" />
      </div>

      <!-- Form Card -->
      <div>
        <VForm ref="form" v-model="valid" lazy-validation>
          <!-- SECTION 1: PERSONAL INFO -->
          <h2 class="step-title mb-6">1. Claimant — Personal info</h2>

          <VRow>
            <VCol cols="12" sm="4">
              <VSelect
                name="salutation"
                v-model="formData.client.title"
                label="Salutation"
                :items="titleOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="4">
              <VTextField
                name="firstName"
                v-capitalize
                v-model="formData.client.firstName"
                label="First Name"
                placeholder="e.g. Mary"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="4">
              <VTextField
                name="lastName"
                v-capitalize
                v-model="formData.client.lastName"
                label="Last Name"
                placeholder="e.g. O'Sullivan"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                name="middleName"
                v-capitalize
                v-model="formData.client.middleName"
                label="Middle Name(s)"
                placeholder="eg. Walsh"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                name="maidenName"
                v-capitalize
                v-model="formData.client.maidenName"
                label="Maiden Names"
                placeholder="Full maiden name(s)"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VDateInput
                name="dob"
                v-model="formData.client.dob"
                label="Date of Birth"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                input-format="dd/mm/yyyy"
                v-auto-slash
                :max="maxDate"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VSelect
                name="maritalStatus"
                v-model="formData.client.maritalStatus"
                label="Marital status"
                :items="maritalStatusOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                name="nationality"
                v-capitalize
                v-model="formData.client.nationality"
                label="Nationality (ie: Passport)"
                placeholder="e.g. Which passport have you?"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="6" v-if="['Divorced', 'Separated'].includes(formData.client.maritalStatus)">
              <VDateInput
                name="dateOfDivorce"
                v-model="formData.client.dateOfDivorce"
                label="Date of Divorce / Civil Dissolution"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                input-format="dd/mm/yyyy"
                v-auto-slash
                :max="dateToday"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12"><div class="field-label">Address</div></VCol>
            <VCol cols="12" sm="6">
              <VSelect
                name="country"
                v-model="formData.client.country"
                label="Country"
                :items="countryOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
                @input="resetClientVerification()"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                name="postcode"
                v-capitalize
                v-model="formData.client.postcode"
                label="Postcode"
                placeholder="e.g. F28 AE22"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
                @input="resetClientVerification()"
              />
            </VCol>
            <VCol v-if="formData.client.country" cols="12">
              <AddressSearch :country="formData.client.country || 'GBR'" @address-selected="onClientAddressSelected">
              </AddressSearch>
            </VCol>
            <VCol cols="12">
              <VTextField
                name="address1"
                v-capitalize
                v-model="formData.client.address1"
                label="Address 1"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
                @input="resetClientVerification()"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                name="address2"
                v-capitalize
                v-model="formData.client.address2"
                label="Address 2"
                variant="outlined"
                density="comfortable"
                :disabled="isLoadingSave"
                @input="resetClientVerification()"
              />
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                name="city"
                v-capitalize
                v-model="formData.client.city"
                label="City"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
                @input="resetClientVerification()"
              />
            </VCol>
            <VCol v-if="hasStateClientOptions" cols="12" sm="6">
              <VSelect
                name="state"
                variant="outlined"
                density="comfortable"
                color="primary"
                label="State"
                v-model="formData.client.state"
                :items="stateClientOptions"
                :disabled="isLoadingSave || !formData.client.country"
                :rules="[rules.required]"
              />
            </VCol>
            <VCol cols="12">
              <div class="d-flex align-center gap-2 flex-wrap mb-4">
                <VBtn
                  color="teal"
                  size="small"
                  :loading="isValidatingAddress"
                  @click="verifyClientAddress(false)"
                  :disabled="!formData.client.address1 || !formData.client.city"
                >
                  Verify Address
                </VBtn>
                <VChip
                  v-bind="props"
                  :color="getVerificationColor(formData.client)"
                  size="small"
                  class="address-validation-chip"
                >
                  <VIcon size="small" left>{{ getVerificationIcon(formData.client) }}</VIcon>
                  {{ getVerificationStatus(formData.client) }}
                </VChip>
                <span v-if="formData.client.isPartial" class="text-warning text-caption">
                  <VIcon size="small" color="warning">mdi-alert-circle</VIcon>
                  Partially verified
                </span>
                <span v-if="isValidatingAddress" class="text-caption text-grey">
                  <VProgressCircular indeterminate size="16" width="2" class="mr-1" />
                  Validating...
                </span>
              </div>
            </VCol>

            <VCol cols="12" sm="4">
              <VTextField
                name="phone"
                type="phone"
                v-model="formData.client.tel"
                label="Tel"
                placeholder="e.g. +353 87 1234567"
                variant="outlined"
                :rules="[rules.required, rules.phone]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12" sm="4">
              <VTextField
                name="email"
                v-model="formData.client.email"
                label="Email"
                type="email"
                placeholder="e.g. name@example.com"
                variant="outlined"
                :rules="[rules.required, rules.email]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <!-- <VCol cols="12" sm="4">
              <VTextField  v-capitalize
                v-model="formData.client.pps"
                label="PPS/Social Sec No/TFN"
                placeholder="e.g. 1234567A"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol> -->
            <VCol cols="12" sm="6">
              <VTextField
                name="helperEmail"
                v-model="formData.client.helperEmail"
                label="Helper email (CC'd on every comm)"
                placeholder="e.g. helper@example.com"
                variant="outlined"
                :rules="[rules.friendEmail]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol cols="12">
              <div class="field-label">Ever Live in the UK?</div>
              <VBtnToggle v-model="formData.client.everLivedUK" mandatory color="teal" rounded="lg" border class="mt-4">
                <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
              </VBtnToggle>
            </VCol>
            <VCol v-if="formData.client.everLivedUK" cols="12" sm="6">
              <VDateInput
                name="dateLeftUK"
                v-model="formData.client.dateLeftUK"
                label="Date left UK"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                input-format="dd/mm/yyyy"
                v-auto-slash
                :max="dateToday"
                :disabled="isLoadingSave"
              />
            </VCol>
            <VCol v-if="formData.client.everLivedUK" cols="12" sm="6">
              <VSelect
                name="approxYearsInUK"
                v-model="formData.client.approxYearsInUK"
                label="Approx Years in UK?"
                :items="approxYearsInUKptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                :rules="[rules.required]"
                density="comfortable"
                :disabled="isLoadingSave"
              />
            </VCol>
          </VRow>

          <!-- SECTION 2: TRACK QUESTION -->
          <VDivider class="my-6" />
          <h2 class="step-title mb-2">2. Whose UK work record are we claiming on?</h2>
          <div class="sec-hint mb-4" style="color: var(--n50); font-style: italic">
            Ask exactly: <b>"Who worked in the UK to build up this pension?"</b>
          </div>
          <VRow>
            <!-- Track A: Self-Claim - Show only if in available tracks -->
            <VCol v-if="eligibilityOptions.tracks.includes('A')" cols="12" md="4">
              <VCard
                :class="['track-card', { 'track-selected': formData.track === 'A' }]"
                @click="selectTrack('A')"
                variant="outlined"
                hover
              >
                <VCardItem>
                  <div class="track-radio">
                    <VRadio v-model="formData.track" value="A" />
                    <div>
                      <strong>Self-Claim</strong>
                      <div class="track-hint">Own record · single, married or divorced</div>
                    </div>
                  </div>
                </VCardItem>
              </VCard>
            </VCol>

            <!-- Track B: Claim on Husband - Show only if in available tracks -->
            <VCol v-if="eligibilityOptions.tracks.includes('B')" cols="12" md="4">
              <VCard
                :class="['track-card', { 'track-selected': formData.track === 'B' }]"
                @click="selectTrack('B')"
                variant="outlined"
                hover
              >
                <VCardItem>
                  <div class="track-radio">
                    <VRadio v-model="formData.track" value="B" />
                    <div>
                      <strong>Claim on Husband</strong>
                      <div class="track-hint">Wife or widow claiming on his record</div>
                    </div>
                  </div>
                </VCardItem>
              </VCard>
            </VCol>

            <!-- Track C: Claim on Wife - Show only if in available tracks -->
            <VCol v-if="eligibilityOptions.tracks.includes('C')" cols="12" md="4">
              <VCard
                :class="['track-card', { 'track-selected': formData.track === 'C' }]"
                @click="selectTrack('C')"
                variant="outlined"
                hover
              >
                <VCardItem>
                  <div class="track-radio">
                    <VRadio v-model="formData.track" value="C" />
                    <div>
                      <strong>Claim on Wife</strong>
                      <div class="track-hint">Husband claiming 60% on her record</div>
                    </div>
                  </div>
                </VCardItem>
              </VCard>
            </VCol>
          </VRow>

          <!-- Show message when no tracks are available -->
          <VAlert
            v-if="!eligibilityOptions.eligible && formData.client.title && formData.client.maritalStatus"
            type="error"
            class="mt-4"
          >
            <strong>Not eligible</strong> — Based on your salutation, marital status, and UK residence, you are not
            eligible for any UK State Pension claim.
          </VAlert>

          <!-- Show info when tracks are available but none selected -->
          <VAlert
            v-else-if="eligibilityOptions.eligible && eligibilityOptions.tracks.length > 0 && !formData.track"
            type="info"
            class="mt-4"
          >
            Pick a track to continue. The next questions change based on the answer.
          </VAlert>

          <!-- SECTION 3A: TRACK A (Own Record) -->
          <template v-if="formData.track === 'A'">
            <VDivider class="my-6" />
            <h2 class="step-title mb-2">3. Your UK record</h2>
            <div class="sec-hint mb-4" style="color: var(--n50); font-style: italic">
              You worked in the UK yourself. Capture NINO if known — otherwise we'll trace via UK employers and
              addresses.
            </div>

            <VRow>
              <VCol cols="12">
                <div class="field-label">Are you getting a UK State Pension now?</div>
                <VBtnToggle
                  v-model="formData.trackA.isGettingUKStatePensionNow"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-2"
                >
                  <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                  <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                </VBtnToggle>
              </VCol>

              <VCol cols="12">
                <div class="field-label">Do you have your UK National Insurance Number?</div>
                <VBtnToggle v-model="formData.trackA.hasNino" mandatory color="teal" rounded="lg" border class="mt-2">
                  <VBtn :value="'Yes'" size="small" :disabled="isLoadingSave">Yes — I have it</VBtn>
                  <VBtn
                    v-if="!(['Widowed'].includes(maritalStatus) && formData.trackA.isGettingUKStatePensionNow)"
                    :value="'Forgot'"
                    size="small"
                    :disabled="isLoadingSave"
                    >Forgot / lost it</VBtn
                  >
                </VBtnToggle>
              </VCol>

              <VCol cols="12" sm="6" v-if="formData.trackA.hasNino === 'Yes'">
                <VTextField
                  v-capitalize
                  v-model="formData.trackA.nino"
                  label="NINO"
                  placeholder="AB123456C"
                  variant="outlined"
                  :rules="[rules.required, rules.nino]"
                  density="comfortable"
                  :disabled="isLoadingSave"
                  style="text-transform: uppercase"
                  name="nino"
                />
              </VCol>
            </VRow>

            <!-- Trace block for Track A -->
            <div
              v-if="formData.trackA.hasNino === 'Forgot' && !formData.trackA.isGettingUKStatePensionNow"
              class="mt-4"
            >
              <VAlert type="info" class="mb-4">
                <strong>Golden rule:</strong> NINO unknown → we MUST collect UK employer names + past UK addresses so
                DWP can trace it. At least 1 of each.
              </VAlert>

              <h4 class="mb-3" style="color: var(--n90)">UK Employers</h4>
              <div v-for="(employer, index) in formData.trackA.employers" :key="'empA-' + index" class="rep-item mb-3">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="rep-number">Employer {{ index + 1 }}</span>
                  <VBtn
                    v-if="formData.trackA.employers.length > 1"
                    icon="mdi-close"
                    variant="text"
                    size="small"
                    @click="removeEmployer('A', index)"
                    :disabled="isLoadingSave"
                  />
                </div>
                <VRow>
                  <VCol cols="12" sm="4">
                    <VTextField
                      v-capitalize
                      v-model="employer.name"
                      label="Employer name"
                      placeholder="e.g. Twiflex Limited"
                      variant="outlined"
                      density="compact"
                      :rules="[rules.required]"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12" sm="4">
                    <VDateInput
                      v-model="employer.fromDate"
                      label="From"
                      variant="outlined"
                      density="compact"
                      :rules="[rules.required]"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      input-format="dd/mm/yyyy"
                      v-auto-slash
                      :max="dateToday"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12" sm="4">
                    <VDateInput
                      v-model="employer.toDate"
                      label="To"
                      variant="outlined"
                      density="compact"
                      :rules="[
                        rules.required,
                        (v) =>
                          !employer.fromDate || !v || v >= employer.fromDate || 'End date must be after start date.'
                      ]"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      input-format="dd/mm/yyyy"
                      v-auto-slash
                      :max="dateToday"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VSelect
                      v-model="employer.country"
                      label="Country"
                      :items="countryUKOptions"
                      item-title="label"
                      item-value="value"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      readonly
                    />
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VTextField
                      v-capitalize
                      v-model="employer.postcode"
                      label="Postcode"
                      placeholder="e.g. F28 AE22"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetEmployerVerification('A', index)"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-capitalize
                      v-model="employer.address1"
                      label="Address 1"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetEmployerVerification('A', index)"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-capitalize
                      v-model="employer.address2"
                      label="Address 2"
                      variant="outlined"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetEmployerVerification('A', index)"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-capitalize
                      v-model="employer.city"
                      label="City"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetEmployerVerification('A', index)"
                    />
                  </VCol>

                  <!-- Employer Address Verification Button -->
                  <!-- <VCol cols="12">
                    <div class="d-flex align-center gap-2 flex-wrap">
                      <VBtn
                        color="teal"
                        size="small"
                        :loading="isValidatingAddress"
                        @click="verifyEmployerAddress('A', index)"
                        :disabled="!employer.address1 || !employer.city"
                      >
                        Verify Address
                      </VBtn>
                      <VChip
                        v-bind="props"
                        :color="getVerificationColor(employer)"
                        size="x-small"
                        class="address-validation-chip"
                      >
                        <VIcon size="x-small" left>{{ getVerificationIcon(employer) }}</VIcon>
                        {{ getVerificationStatus(employer) }}
                      </VChip>
                      <span v-if="employer.wasCleansed" class="text-warning text-caption">
                        <VIcon size="x-small" color="warning">mdi-alert</VIcon>
                        Corrected
                      </span>
                      <span v-if="isValidatingAddress" class="text-caption text-grey">
                        <VProgressCircular indeterminate size="12" width="2" class="mr-1" />
                        Validating...
                      </span>
                    </div>
                  </VCol> -->
                </VRow>
              </div>
              <VBtn
                variant="outlined"
                color="teal"
                prepend-icon="mdi-plus"
                rounded="lg"
                @click="addEmployer('A')"
                :disabled="isLoadingSave"
              >
                Add another UK employer
              </VBtn>

              <h4 class="mt-4 mb-3" style="color: var(--n90)">Past UK HOME Address(es)</h4>
              <div v-for="(address, index) in formData.trackA.addresses" :key="'addrA-' + index" class="rep-item mb-3">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="rep-number">Address {{ index + 1 }}</span>
                  <VBtn
                    v-if="formData.trackA.addresses.length > 1"
                    icon="mdi-close"
                    variant="text"
                    size="small"
                    @click="removeAddress('A', index)"
                    :disabled="isLoadingSave"
                  />
                </div>
                <VRow>
                  <VCol cols="12" sm="6">
                    <VSelect
                      v-model="address.country"
                      label="Country"
                      :items="countryUKOptions"
                      item-title="label"
                      item-value="value"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      readonly
                    />
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VTextField
                      v-capitalize
                      v-model="address.postcode"
                      label="Postcode"
                      placeholder="e.g. F28 AE22"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetPastAddressVerification('A', index)"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-capitalize
                      v-model="address.address1"
                      label="Address 1"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetPastAddressVerification('A', index)"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-capitalize
                      v-model="address.address2"
                      label="Address 2"
                      variant="outlined"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetPastAddressVerification('A', index)"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-capitalize
                      v-model="address.city"
                      label="City"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetPastAddressVerification('A', index)"
                    />
                  </VCol>

                  <!-- Past Address Verification Button -->
                  <!-- <VCol cols="12">
                    <div class="d-flex align-center gap-2 flex-wrap">
                      <VBtn
                        color="teal"
                        size="small"
                        :loading="isValidatingAddress"
                        @click="verifyPastAddress('A', index)"
                        :disabled="!address.address1 || !address.city"
                      >
                        Verify Address
                      </VBtn>
                      <VChip
                        v-bind="props"
                        :color="getVerificationColor(address)"
                        size="x-small"
                        class="address-validation-chip"
                      >
                        <VIcon size="x-small" left>{{ getVerificationIcon(address) }}</VIcon>
                        {{ getVerificationStatus(address) }}
                      </VChip>
                      <span v-if="address.wasCleansed" class="text-warning text-caption">
                        <VIcon size="x-small" color="warning">mdi-alert</VIcon>
                        Corrected
                      </span>
                      <span v-if="isValidatingAddress" class="text-caption text-grey">
                        <VProgressCircular indeterminate size="12" width="2" class="mr-1" />
                        Validating...
                      </span>
                    </div>
                  </VCol> -->

                  <VCol cols="12" sm="6">
                    <VDateInput
                      v-model="address.fromDate"
                      label="From"
                      variant="outlined"
                      density="compact"
                      :rules="[rules.required]"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      input-format="dd/mm/yyyy"
                      v-auto-slash
                      :max="dateToday"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VDateInput
                      v-model="address.toDate"
                      label="To"
                      variant="outlined"
                      density="compact"
                      :rules="[
                        rules.required,
                        (v) => !address.fromDate || !v || v >= address.fromDate || 'End date must be after start date.'
                      ]"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      input-format="dd/mm/yyyy"
                      v-auto-slash
                      :max="dateToday"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                </VRow>
              </div>
              <VBtn
                variant="outlined"
                color="teal"
                prepend-icon="mdi-plus"
                rounded="lg"
                @click="addAddress('A')"
                :disabled="isLoadingSave"
              >
                Add another UK address
              </VBtn>
            </div>
          </template>

          <!-- SECTION 3B: TRACK B (Wife/Widow) -->
          <template v-if="formData.track === 'B'">
            <VDivider class="my-6" />
            <h2 class="step-title mb-2">3. Husband's UK record</h2>
            <div class="sec-hint mb-4" style="color: var(--n50); font-style: italic">
              You're claiming on your husband's UK record (or widow). His NINO box is what matters — yours stays blank.
            </div>

            <VAlert v-if="!formData.client.everLivedUK" type="info" class="mb-4">
              <strong>Your NINO stays blank.</strong> You never worked in the UK, so we leave the wife's NINO box empty.
            </VAlert>

            <VRow>
              <VCol cols="12">
                <div class="field-label">Is your husband alive?</div>
                <VBtnToggle
                  v-model="formData.trackB.husbandAlive"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-2"
                >
                  <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                  <VBtn :value="false" size="small" :disabled="isLoadingSave">No — Widow</VBtn>
                </VBtnToggle>
              </VCol>

              <VCol cols="12" v-if="formData.trackB.husbandAlive === false">
                <VAlert type="warning">
                  <strong>Widow track</strong>
                  We leave both NINO boxes blank but still send the LoA. <b>Mandatory:</b> husband's full UK work
                  history + the towns he lived in.
                </VAlert>
              </VCol>

              <VCol cols="12" v-if="formData.trackB.husbandAlive === true">
                <div class="field-label">Is your husband getting a UK State Pension now?</div>
                <VBtnToggle
                  v-model="formData.trackB.husbandGettinStatePensionNow"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-2"
                >
                  <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                  <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                </VBtnToggle>
              </VCol>

              <VCol cols="12" v-else>
                <div class="field-label">Are you getting a UK State Pension now from your husband?</div>
                <VBtnToggle
                  v-model="formData.trackB.youGettinStatePensionNow"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-2"
                >
                  <VBtn :value="true" size="small" :disabled="isLoadingSave">Yes</VBtn>
                  <VBtn :value="false" size="small" :disabled="isLoadingSave">No</VBtn>
                </VBtnToggle>
              </VCol>

              <VCol cols="12" sm="4">
                <VSelect
                  v-model="formData.trackB.title"
                  label="Husband's salutation"
                  :items="titleHusbandOptions"
                  item-title="label"
                  item-value="value"
                  variant="outlined"
                  :rules="[rules.required]"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12" sm="4">
                <VTextField
                  v-capitalize
                  v-model="formData.trackB.husbandFirstName"
                  label="Husband's first name"
                  placeholder="e.g. Patrick"
                  variant="outlined"
                  :rules="[rules.required]"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12" sm="4">
                <VTextField
                  v-capitalize
                  v-model="formData.trackB.husbandSurname"
                  label="Husband's surname name"
                  placeholder="e.g. O'Sullivan"
                  variant="outlined"
                  :rules="[rules.required]"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VTextField
                  v-capitalize
                  v-model="formData.trackB.husbandOtherName"
                  label="Husband's other names (if any)"
                  placeholder=""
                  variant="outlined"
                  density="comfortable"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VDateInput
                  v-model="formData.trackB.husbandDob"
                  label="Husband's DOB"
                  variant="outlined"
                  :rules="[rules.required]"
                  density="comfortable"
                  prepend-icon=""
                  prepend-inner-icon="mdi-calendar"
                  input-format="dd/mm/yyyy"
                  v-auto-slash
                  :max="maxDate"
                  :disabled="isLoadingSave"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VDateInput
                  v-model="formData.trackB.marriageDate"
                  label="Date you married"
                  variant="outlined"
                  :rules="[rules.required]"
                  density="comfortable"
                  prepend-icon=""
                  prepend-inner-icon="mdi-calendar"
                  input-format="dd/mm/yyyy"
                  v-auto-slash
                  :max="dateToday"
                  :disabled="isLoadingSave"
                />
              </VCol>

              <VCol cols="12" sm="6">
                <VDateInput
                  v-model="formData.trackB.husbandLeftUK"
                  label="Date husband left UK"
                  variant="outlined"
                  density="comfortable"
                  prepend-icon=""
                  prepend-inner-icon="mdi-calendar"
                  input-format="dd/mm/yyyy"
                  v-auto-slash
                  :max="dateToday"
                  :disabled="isLoadingSave"
                />
              </VCol>

              <VCol cols="12">
                <div class="field-label">Husband’s Current Address</div>
                <VBtnToggle
                  v-model="formData.trackB.sameAddress"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-2 mb-3"
                >
                  <VBtn :value="true" size="small" :disabled="isLoadingSave">Same as yours</VBtn>
                  <VBtn :value="false" size="small" :disabled="isLoadingSave">Different address</VBtn>
                </VBtnToggle>
              </VCol>

              <template v-if="!formData.trackB.sameAddress">
                <VCol cols="12" sm="6">
                  <VSelect
                    v-model="formData.trackB.country"
                    label="Country"
                    :items="countryOptions"
                    item-title="label"
                    item-value="value"
                    variant="outlined"
                    :rules="[rules.required]"
                    density="comfortable"
                    :disabled="isLoadingSave"
                  />
                </VCol>
                <VCol cols="12" sm="6">
                  <VTextField
                    v-capitalize
                    v-model="formData.trackB.postcode"
                    label="Postcode"
                    placeholder="e.g. F28 AE22"
                    variant="outlined"
                    :rules="[rules.required]"
                    density="comfortable"
                    :disabled="isLoadingSave"
                    @input="resetPartnerVerification('trackB')"
                  />
                </VCol>
                <VCol v-if="formData.trackB.country" cols="12">
                  <AddressSearch
                    :country="formData.trackB.country || 'GBR'"
                    @address-selected="onTrackBAddressSelected"
                  >
                  </AddressSearch>
                </VCol>
                <VCol cols="12">
                  <VTextField
                    v-capitalize
                    v-model="formData.trackB.address1"
                    label="Address 1"
                    variant="outlined"
                    :rules="[rules.required]"
                    density="comfortable"
                    :disabled="isLoadingSave"
                    @input="resetPartnerVerification('trackB')"
                  />
                </VCol>
                <VCol cols="12">
                  <VTextField
                    v-capitalize
                    v-model="formData.trackB.address2"
                    label="Address 2"
                    variant="outlined"
                    density="comfortable"
                    :disabled="isLoadingSave"
                    @input="resetPartnerVerification('trackB')"
                  />
                </VCol>
                <VCol cols="12" sm="6">
                  <VTextField
                    v-capitalize
                    v-model="formData.trackB.city"
                    label="City"
                    variant="outlined"
                    :rules="[rules.required]"
                    density="comfortable"
                    :disabled="isLoadingSave"
                    @input="resetPartnerVerification('trackB')"
                  />
                </VCol>
                <VCol v-if="hasStateTrackBOptions" cols="12" sm="6">
                  <VSelect
                    variant="outlined"
                    density="comfortable"
                    color="primary"
                    name="state"
                    label="State"
                    v-model="formData.trackB.state"
                    :items="stateTrackBOptions"
                    :disabled="isLoadingSave || !formData.trackB.country"
                    :rules="[rules.required]"
                  />
                </VCol>
                <!-- Partner B Address Verification Button -->
                <VCol cols="12">
                  <div class="d-flex align-center gap-2 flex-wrap">
                    <VBtn
                      color="teal"
                      size="small"
                      :loading="isValidatingAddress"
                      @click="verifyPartnerBAddress"
                      :disabled="!formData.trackB.address1 || !formData.trackB.city"
                    >
                      Verify Address
                    </VBtn>
                    <VChip
                      v-bind="props"
                      :color="getVerificationColor(formData.trackB)"
                      size="x-small"
                      class="address-validation-chip"
                    >
                      <VIcon size="x-small" left>{{ getVerificationIcon(formData.trackB) }}</VIcon>
                      {{ getVerificationStatus(formData.trackB) }}
                    </VChip>
                    <span v-if="formData.trackB.wasCleansed" class="text-warning text-caption">
                      <VIcon size="x-small" color="warning">mdi-alert</VIcon>
                      Corrected
                    </span>
                    <span v-if="isValidatingAddress" class="text-caption text-grey">
                      <VProgressCircular indeterminate size="12" width="2" class="mr-1" />
                      Validating...
                    </span>
                  </div>
                </VCol>
              </template>

              <VCol cols="12">
                <div class="field-label">What is Your/Husband's NINO?</div>
                <VBtnToggle
                  v-model="formData.trackB.husbandHasNino"
                  mandatory
                  color="teal"
                  rounded="lg"
                  border
                  class="mt-2"
                >
                  <VBtn :value="'Yes'" size="small" :disabled="isLoadingSave">Yes — he has it</VBtn>
                  <VBtn :value="'Forgot'" size="small" :disabled="isLoadingSave">Forgot / lost</VBtn>
                </VBtnToggle>
              </VCol>

              <VCol cols="12" sm="6" v-if="formData.trackB.husbandHasNino === 'Yes'">
                <VTextField
                  v-capitalize
                  v-model="formData.trackB.husbandNino"
                  label="Husband's NINO"
                  placeholder="AB123456C"
                  variant="outlined"
                  :rules="[rules.required, rules.nino]"
                  density="comfortable"
                  :disabled="isLoadingSave"
                  style="text-transform: uppercase"
                  name="nino"
                />
              </VCol>
            </VRow>

            <!-- Trace block for Track B -->
            <div
              v-if="formData.trackB.husbandHasNino === 'Forgot' || formData.trackB.husbandAlive === false"
              class="mt-4"
            >
              <VAlert type="info" class="mb-4">
                <strong>Golden rule:</strong> Husband's NINO unknown (or widow) → MUST collect his UK employers +
                addresses.
              </VAlert>

              <h4 class="mb-3" style="color: var(--n90)">Husband's UK Employers</h4>
              <div v-for="(employer, index) in formData.trackB.employers" :key="'empB-' + index" class="rep-item mb-3">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="rep-number">Employer {{ index + 1 }}</span>
                  <VBtn
                    v-if="formData.trackB.employers.length > 1"
                    icon="mdi-close"
                    variant="text"
                    size="small"
                    @click="removeEmployer('B', index)"
                    :disabled="isLoadingSave"
                  />
                </div>
                <VRow>
                  <VCol cols="12" sm="4">
                    <VTextField
                      v-capitalize
                      v-model="employer.name"
                      label="Employer name"
                      placeholder="e.g. Twiflex Limited"
                      variant="outlined"
                      density="compact"
                      :rules="[rules.required]"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12" sm="4">
                    <VDateInput
                      v-model="employer.fromDate"
                      label="From"
                      variant="outlined"
                      density="compact"
                      :rules="[rules.required]"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      input-format="dd/mm/yyyy"
                      v-auto-slash
                      :max="dateToday"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12" sm="4">
                    <VDateInput
                      v-model="employer.toDate"
                      label="To"
                      variant="outlined"
                      density="compact"
                      :rules="[
                        rules.required,
                        (v) =>
                          !employer.fromDate || !v || v >= employer.fromDate || 'End date must be after start date.'
                      ]"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      input-format="dd/mm/yyyy"
                      v-auto-slash
                      :max="dateToday"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VSelect
                      v-model="employer.country"
                      label="Country"
                      :items="countryUKOptions"
                      item-title="label"
                      item-value="value"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      readonly
                    />
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VTextField
                      v-capitalize
                      v-model="employer.postcode"
                      label="Postcode"
                      placeholder="e.g. F28 AE22"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetEmployerVerification('B', index)"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-capitalize
                      v-model="employer.address1"
                      label="Address 1"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetEmployerVerification('B', index)"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-capitalize
                      v-model="employer.address2"
                      label="Address 2"
                      variant="outlined"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetEmployerVerification('B', index)"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-capitalize
                      v-model="employer.city"
                      label="City"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetEmployerVerification('B', index)"
                    />
                  </VCol>

                  <!-- Employer Verification Button -->
                  <!-- <VCol cols="12">
                    <div class="d-flex align-center gap-2 flex-wrap">
                      <VBtn
                        color="teal"
                        size="small"
                        :loading="isValidatingAddress"
                        @click="verifyEmployerAddress('B', index)"
                        :disabled="!employer.address1 || !employer.city"
                      >
                        Verify Address
                      </VBtn>
                      <VChip
                        v-bind="props"
                        :color="getVerificationColor(employer)"
                        size="x-small"
                        class="address-validation-chip"
                      >
                        <VIcon size="x-small" left>{{ getVerificationIcon(employer) }}</VIcon>
                        {{ getVerificationStatus(employer) }}
                      </VChip>
                      <span v-if="employer.wasCleansed" class="text-warning text-caption">
                        <VIcon size="x-small" color="warning">mdi-alert</VIcon>
                        Corrected
                      </span>
                      <span v-if="isValidatingAddress" class="text-caption text-grey">
                        <VProgressCircular indeterminate size="12" width="2" class="mr-1" />
                        Validating...
                      </span>
                    </div>
                  </VCol> -->
                </VRow>
              </div>
              <VBtn
                variant="outlined"
                color="teal"
                prepend-icon="mdi-plus"
                rounded="lg"
                @click="addEmployer('B')"
                :disabled="isLoadingSave"
              >
                Add another UK employer
              </VBtn>

              <h4 class="mt-4 mb-3" style="color: var(--n90)">Husband's Past UK HOME Address(es)</h4>
              <div v-for="(address, index) in formData.trackB.addresses" :key="'addrB-' + index" class="rep-item mb-3">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="rep-number">Address {{ index + 1 }}</span>
                  <VBtn
                    v-if="formData.trackB.addresses.length > 1"
                    icon="mdi-close"
                    variant="text"
                    size="small"
                    @click="removeAddress('B', index)"
                    :disabled="isLoadingSave"
                  />
                </div>
                <VRow>
                  <VCol cols="12" sm="6">
                    <VSelect
                      v-model="address.country"
                      label="Country"
                      :items="countryUKOptions"
                      item-title="label"
                      item-value="value"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      readonly
                    />
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VTextField
                      v-capitalize
                      v-model="address.postcode"
                      label="Postcode"
                      placeholder="e.g. F28 AE22"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetPastAddressVerification('B', index)"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-capitalize
                      v-model="address.address1"
                      label="Address 1"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetPastAddressVerification('B', index)"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-capitalize
                      v-model="address.address2"
                      label="Address 2"
                      variant="outlined"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetPastAddressVerification('B', index)"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-capitalize
                      v-model="address.city"
                      label="City"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetPastAddressVerification('B', index)"
                    />
                  </VCol>

                  <!-- Past Address Verification Button -->
                  <!-- <VCol cols="12">
                    <div class="d-flex align-center gap-2 flex-wrap">
                      <VBtn
                        color="teal"
                        size="small"
                        :loading="isValidatingAddress"
                        @click="verifyPastAddress('B', index)"
                        :disabled="!address.address1 || !address.city"
                      >
                        Verify Address
                      </VBtn>
                      <VChip
                        v-bind="props"
                        :color="getVerificationColor(address)"
                        size="x-small"
                        class="address-validation-chip"
                      >
                        <VIcon size="x-small" left>{{ getVerificationIcon(address) }}</VIcon>
                        {{ getVerificationStatus(address) }}
                      </VChip>
                      <span v-if="address.wasCleansed" class="text-warning text-caption">
                        <VIcon size="x-small" color="warning">mdi-alert</VIcon>
                        Corrected
                      </span>
                      <span v-if="isValidatingAddress" class="text-caption text-grey">
                        <VProgressCircular indeterminate size="12" width="2" class="mr-1" />
                        Validating...
                      </span>
                    </div>
                  </VCol> -->

                  <VCol cols="12" sm="6">
                    <VDateInput
                      v-model="address.fromDate"
                      label="From"
                      variant="outlined"
                      density="compact"
                      :rules="[rules.required]"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      input-format="dd/mm/yyyy"
                      v-auto-slash
                      :max="dateToday"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VDateInput
                      v-model="address.toDate"
                      label="To"
                      variant="outlined"
                      density="compact"
                      :rules="[
                        rules.required,
                        (v) => !address.fromDate || !v || v >= address.fromDate || 'End date must be after start date.'
                      ]"
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar"
                      input-format="dd/mm/yyyy"
                      v-auto-slash
                      :max="dateToday"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                </VRow>
              </div>
              <VBtn
                variant="outlined"
                color="teal"
                prepend-icon="mdi-plus"
                rounded="lg"
                @click="addAddress('B')"
                :disabled="isLoadingSave"
              >
                Add another UK address
              </VBtn>

              <VRow class="mt-4">
                <VCol cols="12">
                  <VTextField
                    v-capitalize
                    v-model="formData.trackB.townsLived"
                    label="Towns / cities husband lived in"
                    placeholder="e.g. Liverpool 1962–1968, Manchester 1968–1978"
                    variant="outlined"
                    :rules="formData.trackB.husbandAlive === false ? [rules.required] : []"
                    density="comfortable"
                    :disabled="isLoadingSave"
                    rows="2"
                    textarea
                  />
                </VCol>
              </VRow>
            </div>
          </template>
          <!-- SECTION 3C: TRACK C (Husband on Wife's, 60%) -->
          <template v-if="formData.track === 'C'">
            <VDivider class="my-6" />
            <h2 class="step-title mb-2">3. Wife's UK record (60% rule)</h2>
            <div class="sec-hint mb-4" style="color: var(--n50); font-style: italic">
              You're claiming 60% of your wife's UK State Pension. This only works for a narrow window of dates — we
              check first.
            </div>

            <VAlert type="error" class="mb-4">
              <strong>Gate — check first</strong>
              Wife's State Pension Age must fall <b>6 Apr 2010 - 5 Apr 2016</b>. Enter wife's DOB below and we'll check
              automatically.
            </VAlert>

            <VRow>
              <VCol cols="12" sm="6">
                <VDateInput
                  v-model="formData.trackC.wifeDob"
                  label="Wife's DOB"
                  variant="outlined"
                  :rules="[rules.required]"
                  density="comfortable"
                  prepend-icon=""
                  prepend-inner-icon="mdi-calendar"
                  input-format="dd/mm/yyyy"
                  v-auto-slash
                  :max="maxDate"
                  :disabled="isLoadingSave"
                  @update:model-value="checkGateC"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VDateInput
                  v-model="formData.client.dob"
                  label="Husband's DOB"
                  variant="outlined"
                  :rules="[rules.required]"
                  density="comfortable"
                  prepend-icon=""
                  prepend-inner-icon="mdi-calendar"
                  input-format="dd/mm/yyyy"
                  v-auto-slash
                  :max="maxDate"
                  :disabled="isLoadingSave"
                  readonly
                />
              </VCol>
            </VRow>

            <!-- Gate result -->
            <VRow v-if="formData.trackC.gateResult">
              <VCol cols="12">
                <VAlert :type="formData.trackC.gatePassed ? 'success' : 'error'">
                  <strong>{{ formData.trackC.gatePassed ? '✓ Gate passed.' : '🛑 Gate FAILED.' }}</strong>
                  <span v-if="formData.trackC.gatePassed">
                    Wife's SPA ({{ formData.trackC.spaDate }}) is within 6 Apr 2010 – 5 Apr 2016. Continue.
                  </span>
                  <span v-else>
                    Wife's SPA ({{ formData.trackC.spaDate }}) is outside 6 Apr 2010 – 5 Apr 2016. This case is not
                    eligible for the 60% rule. Do not proceed.
                  </span>
                </VAlert>
              </VCol>
            </VRow>

            <!-- Track C rest (only if gate passed) -->
            <template v-if="formData.trackC.gatePassed">
              <VAlert type="info" class="mb-4 mt-4">
                <strong>Your NINO stays blank.</strong> You never worked in the UK, so we leave the husband's NINO box
                empty.
              </VAlert>

              <VRow>
                <VCol cols="12" sm="6">
                  <VDateInput
                    v-model="formData.trackC.marriageDate"
                    label="Date you married"
                    variant="outlined"
                    :rules="[rules.required]"
                    density="comfortable"
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar"
                    input-format="dd/mm/yyyy"
                    v-auto-slash
                    :max="dateToday"
                    :disabled="isLoadingSave"
                  />
                </VCol>

                <VCol cols="12" sm="6">
                  <VDateInput
                    v-model="formData.trackC.wifeLeftUK"
                    label="Date wife left UK"
                    variant="outlined"
                    :rules="[rules.required]"
                    density="comfortable"
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar"
                    input-format="dd/mm/yyyy"
                    v-auto-slash
                    :max="dateToday"
                    :disabled="isLoadingSave"
                  />
                </VCol>

                <VCol cols="12">
                  <div class="field-label">Wife's last UK address</div>
                  <VBtnToggle
                    v-model="formData.trackC.sameAddress"
                    mandatory
                    color="teal"
                    rounded="lg"
                    border
                    class="mt-2 mb-3"
                  >
                    <VBtn :value="true" size="small" :disabled="isLoadingSave">Same as yours</VBtn>
                    <VBtn :value="false" size="small" :disabled="isLoadingSave">Different address</VBtn>
                  </VBtnToggle>
                </VCol>
                <template v-if="!formData.trackC.sameAddress">
                  <VCol cols="12" sm="6">
                    <VSelect
                      v-model="formData.trackC.country"
                      label="Country"
                      :items="countryOptions"
                      item-title="label"
                      item-value="value"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                    />
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VTextField
                      v-capitalize
                      v-model="formData.trackC.postcode"
                      label="Postcode"
                      placeholder="e.g. F28 AE22"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetPartnerVerification('trackC')"
                    />
                  </VCol>
                  <VCol v-if="formData.trackC.country" cols="12">
                    <AddressSearch
                      :country="formData.trackC.country || 'GBR'"
                      @address-selected="onTrackCAddressSelected"
                    >
                    </AddressSearch>
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-capitalize
                      v-model="formData.trackC.address1"
                      label="Address 1"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetPartnerVerification('trackC')"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-capitalize
                      v-model="formData.trackC.address2"
                      label="Address 2"
                      variant="outlined"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetPartnerVerification('trackC')"
                    />
                  </VCol>
                  <VCol cols="12" sm="6">
                    <VTextField
                      v-capitalize
                      v-model="formData.trackC.city"
                      label="City"
                      variant="outlined"
                      :rules="[rules.required]"
                      density="comfortable"
                      :disabled="isLoadingSave"
                      @input="resetPartnerVerification('trackC')"
                    />
                  </VCol>
                  <VCol v-if="hasStateTrackCOptions" cols="12" sm="6">
                    <VSelect
                      variant="outlined"
                      density="comfortable"
                      color="primary"
                      name="state"
                      label="State"
                      v-model="formData.trackC.state"
                      :items="stateTrackCOptions"
                      :disabled="isLoadingSave || !formData.trackC.country"
                      :rules="[rules.required]"
                    />
                  </VCol>
                  <!-- Partner C Address Verification Button -->
                  <VCol cols="12">
                    <div class="d-flex align-center gap-2 flex-wrap">
                      <VBtn
                        color="teal"
                        size="small"
                        :loading="isValidatingAddress"
                        @click="verifyPartnerCAddress"
                        :disabled="!formData.trackC.address1 || !formData.trackC.city"
                      >
                        Verify Address
                      </VBtn>
                      <VChip
                        v-bind="props"
                        :color="getVerificationColor(formData.trackC)"
                        size="x-small"
                        class="address-validation-chip"
                      >
                        <VIcon size="x-small" left>{{ getVerificationIcon(formData.trackC) }}</VIcon>
                        {{ getVerificationStatus(formData.trackC) }}
                      </VChip>
                      <span v-if="formData.trackC.wasCleansed" class="text-warning text-caption">
                        <VIcon size="x-small" color="warning">mdi-alert</VIcon>
                        Corrected
                      </span>
                      <span v-if="isValidatingAddress" class="text-caption text-grey">
                        <VProgressCircular indeterminate size="12" width="2" class="mr-1" />
                        Validating...
                      </span>
                    </div>
                  </VCol>
                </template>

                <VCol cols="12">
                  <div class="field-label">Does your wife know / have her NINO?</div>
                  <VBtnToggle
                    v-model="formData.trackC.wifeHasNino"
                    mandatory
                    color="teal"
                    rounded="lg"
                    border
                    class="mt-2"
                  >
                    <VBtn :value="'Yes'" size="small" :disabled="isLoadingSave">Yes — she has it</VBtn>
                    <VBtn
                      v-if="!(['Widowed'].includes(maritalStatus) && formData.trackC.isGettingUKStatePensionNow)"
                      :value="'Forgot'"
                      size="small"
                      :disabled="isLoadingSave"
                      >Forgot / lost</VBtn
                    >
                  </VBtnToggle>
                </VCol>

                <VCol cols="12" sm="6" v-if="formData.trackC.wifeHasNino === 'Yes'">
                  <VTextField
                    v-capitalize
                    v-model="formData.trackC.wifeNino"
                    label="Wife's NINO"
                    placeholder="AB123456C"
                    variant="outlined"
                    :rules="[rules.required, rules.nino]"
                    density="comfortable"
                    :disabled="isLoadingSave"
                    style="text-transform: uppercase"
                    name="nino"
                  />
                </VCol>
              </VRow>

              <!-- Trace block for Track C -->
              <div v-if="formData.trackC.wifeHasNino === 'Forgot'" class="mt-4">
                <VAlert type="info" class="mb-4">
                  <strong>Golden rule:</strong> Wife's NINO unknown → MUST collect her UK employers + addresses.
                </VAlert>

                <h4 class="mb-3" style="color: var(--n90)">Wife's UK Employers</h4>
                <div
                  v-for="(employer, index) in formData.trackC.employers"
                  :key="'empC-' + index"
                  class="rep-item mb-3"
                >
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span class="rep-number">Employer {{ index + 1 }}</span>
                    <VBtn
                      v-if="formData.trackC.employers.length > 1"
                      icon="mdi-close"
                      variant="text"
                      size="small"
                      @click="removeEmployer('C', index)"
                      :disabled="isLoadingSave"
                    />
                  </div>
                  <VRow>
                    <VCol cols="12" sm="4">
                      <VTextField
                        v-capitalize
                        v-model="employer.name"
                        label="Employer name"
                        placeholder="e.g. Twiflex Limited"
                        variant="outlined"
                        density="compact"
                        :rules="[rules.required]"
                        :disabled="isLoadingSave"
                      />
                    </VCol>
                    <VCol cols="12" sm="4">
                      <VDateInput
                        v-model="employer.fromDate"
                        label="From"
                        variant="outlined"
                        density="compact"
                        :rules="[rules.required]"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar"
                        input-format="dd/mm/yyyy"
                        v-auto-slash
                        :max="dateToday"
                        :disabled="isLoadingSave"
                      />
                    </VCol>
                    <VCol cols="12" sm="4">
                      <VDateInput
                        v-model="employer.toDate"
                        label="To"
                        variant="outlined"
                        density="compact"
                        :rules="[
                          rules.required,
                          (v) =>
                            !employer.fromDate || !v || v >= employer.fromDate || 'End date must be after start date.'
                        ]"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar"
                        input-format="dd/mm/yyyy"
                        v-auto-slash
                        :max="dateToday"
                        :disabled="isLoadingSave"
                      />
                    </VCol>
                    <VCol cols="12" sm="6">
                      <VSelect
                        v-model="employer.country"
                        label="Country"
                        :items="countryUKOptions"
                        item-title="label"
                        item-value="value"
                        variant="outlined"
                        :rules="[rules.required]"
                        density="comfortable"
                        :disabled="isLoadingSave"
                        readonly
                      />
                    </VCol>
                    <VCol cols="12" sm="6">
                      <VTextField
                        v-capitalize
                        v-model="employer.postcode"
                        label="Postcode"
                        placeholder="e.g. F28 AE22"
                        variant="outlined"
                        :rules="[rules.required]"
                        density="comfortable"
                        :disabled="isLoadingSave"
                        @input="resetEmployerVerification('C', index)"
                      />
                    </VCol>
                    <VCol cols="12">
                      <VTextField
                        v-capitalize
                        v-model="employer.address1"
                        label="Address 1"
                        variant="outlined"
                        :rules="[rules.required]"
                        density="comfortable"
                        :disabled="isLoadingSave"
                        @input="resetEmployerVerification('C', index)"
                      />
                    </VCol>
                    <VCol cols="12">
                      <VTextField
                        v-capitalize
                        v-model="employer.address2"
                        label="Address 2"
                        variant="outlined"
                        density="comfortable"
                        :disabled="isLoadingSave"
                        @input="resetEmployerVerification('C', index)"
                      />
                    </VCol>
                    <VCol cols="12">
                      <VTextField
                        v-capitalize
                        v-model="employer.city"
                        label="City"
                        variant="outlined"
                        :rules="[rules.required]"
                        density="comfortable"
                        :disabled="isLoadingSave"
                        @input="resetEmployerVerification('C', index)"
                      />
                    </VCol>

                    <!-- Employer Verification Button -->
                    <!-- <VCol cols="12">
                      <div class="d-flex align-center gap-2 flex-wrap">
                        <VBtn
                          color="teal"
                          size="small"
                          :loading="isValidatingAddress"
                          @click="verifyEmployerAddress('C', index)"
                          :disabled="!employer.address1 || !employer.city"
                        >
                          Verify Address
                        </VBtn>
                        <VChip
                          v-bind="props"
                          :color="getVerificationColor(employer)"
                          size="x-small"
                          class="address-validation-chip"
                        >
                          <VIcon size="x-small" left>{{ getVerificationIcon(employer) }}</VIcon>
                          {{ getVerificationStatus(employer) }}
                        </VChip>
                        <span v-if="employer.wasCleansed" class="text-warning text-caption">
                          <VIcon size="x-small" color="warning">mdi-alert</VIcon>
                          Corrected
                        </span>
                        <span v-if="isValidatingAddress" class="text-caption text-grey">
                          <VProgressCircular indeterminate size="12" width="2" class="mr-1" />
                          Validating...
                        </span>
                      </div>
                    </VCol> -->
                  </VRow>
                </div>
                <VBtn
                  variant="outlined"
                  color="teal"
                  prepend-icon="mdi-plus"
                  rounded="lg"
                  @click="addEmployer('C')"
                  :disabled="isLoadingSave"
                >
                  Add another UK employer
                </VBtn>

                <h4 class="mt-4 mb-3" style="color: var(--n90)">Wife's Past UK HOME Address(es)</h4>
                <div
                  v-for="(address, index) in formData.trackC.addresses"
                  :key="'addrC-' + index"
                  class="rep-item mb-3"
                >
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span class="rep-number">Address {{ index + 1 }}</span>
                    <VBtn
                      v-if="formData.trackC.addresses.length > 1"
                      icon="mdi-close"
                      variant="text"
                      size="small"
                      @click="removeAddress('C', index)"
                      :disabled="isLoadingSave"
                    />
                  </div>
                  <VRow>
                    <VCol cols="12" sm="6">
                      <VSelect
                        v-model="address.country"
                        label="Country"
                        :items="countryUKOptions"
                        item-title="label"
                        item-value="value"
                        variant="outlined"
                        :rules="[rules.required]"
                        density="comfortable"
                        :disabled="isLoadingSave"
                        readonly
                      />
                    </VCol>
                    <VCol cols="12" sm="6">
                      <VTextField
                        v-capitalize
                        v-model="address.postcode"
                        label="Postcode"
                        placeholder="e.g. F28 AE22"
                        variant="outlined"
                        :rules="[rules.required]"
                        density="comfortable"
                        :disabled="isLoadingSave"
                        @input="resetPastAddressVerification('C', index)"
                      />
                    </VCol>
                    <VCol cols="12">
                      <VTextField
                        v-capitalize
                        v-model="address.address1"
                        label="Address 1"
                        variant="outlined"
                        :rules="[rules.required]"
                        density="comfortable"
                        :disabled="isLoadingSave"
                        @input="resetPastAddressVerification('C', index)"
                      />
                    </VCol>
                    <VCol cols="12">
                      <VTextField
                        v-capitalize
                        v-model="address.address2"
                        label="Address 2"
                        variant="outlined"
                        density="comfortable"
                        :disabled="isLoadingSave"
                        @input="resetPastAddressVerification('C', index)"
                      />
                    </VCol>
                    <VCol cols="12">
                      <VTextField
                        v-capitalize
                        v-model="address.city"
                        label="City"
                        variant="outlined"
                        :rules="[rules.required]"
                        density="comfortable"
                        :disabled="isLoadingSave"
                        @input="resetPastAddressVerification('C', index)"
                      />
                    </VCol>

                    <!-- Past Address Verification Button -->
                    <!-- <VCol cols="12">
                      <div class="d-flex align-center gap-2 flex-wrap">
                        <VBtn
                          color="teal"
                          size="small"
                          :loading="isValidatingAddress"
                          @click="verifyPastAddress('C', index)"
                          :disabled="!address.address1 || !address.city"
                        >
                          Verify Address
                        </VBtn>
                        <VChip
                          v-bind="props"
                          :color="getVerificationColor(address)"
                          size="x-small"
                          class="address-validation-chip"
                        >
                          <VIcon size="x-small" left>{{ getVerificationIcon(address) }}</VIcon>
                          {{ getVerificationStatus(address) }}
                        </VChip>
                        <span v-if="address.wasCleansed" class="text-warning text-caption">
                          <VIcon size="x-small" color="warning">mdi-alert</VIcon>
                          Corrected
                        </span>
                        <span v-if="isValidatingAddress" class="text-caption text-grey">
                          <VProgressCircular indeterminate size="12" width="2" class="mr-1" />
                          Validating...
                        </span>
                      </div>
                    </VCol> -->

                    <VCol cols="12" sm="6">
                      <VDateInput
                        v-model="address.fromDate"
                        label="From"
                        variant="outlined"
                        density="compact"
                        :rules="[rules.required]"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar"
                        input-format="dd/mm/yyyy"
                        v-auto-slash
                        :max="dateToday"
                        :disabled="isLoadingSave"
                      />
                    </VCol>
                    <VCol cols="12" sm="6">
                      <VDateInput
                        v-model="address.toDate"
                        label="To"
                        variant="outlined"
                        density="compact"
                        :rules="[
                          rules.required,
                          (v) =>
                            !address.fromDate || !v || v >= address.fromDate || 'End date must be after start date.'
                        ]"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar"
                        input-format="dd/mm/yyyy"
                        v-auto-slash
                        :max="dateToday"
                        :disabled="isLoadingSave"
                      />
                    </VCol>
                  </VRow>
                </div>
                <VBtn
                  variant="outlined"
                  color="teal"
                  prepend-icon="mdi-plus"
                  rounded="lg"
                  @click="addAddress('C')"
                  :disabled="isLoadingSave"
                >
                  Add another UK address
                </VBtn>
              </div>
            </template>
          </template>

          <!-- REASSURANCE & SUBMIT -->
          <VDivider class="my-6" />
          <!-- <div
            v-if="!(formData.trackA.isGettingUKStatePensionNow || formData.trackB.youGettinStatePensionNow)"
            class="reassurance-box pa-4 mb-4"
            style="background: #fffef3; border-left: 4px solid #fbc02d; border-radius: 0 6px 6px 0"
          >
            <strong
              style="
                font-size: 11px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                display: block;
                margin-bottom: 6px;
                color: #7c5400;
              "
            >
              Reassurance script — read aloud if NINO missing
            </strong>
            <span style="font-style: italic; color: #7c5400; font-size: 13px">
              "Don't worry if you don't have your old National Insurance number handy. As long as you can tell us where
              you worked and lived in the UK, our back-office team can structure the application so the DWP traces it
              for you."
            </span>
          </div> -->

          <!-- Navigation -->
          <div class="nav-row mt-8">
            <VSpacer />
            <VBtn
              color="teal"
              rounded="lg"
              size="large"
              append-icon="mdi-send"
              :loading="isLoadingSave"
              @click="submitForm"
            >
              Finish Short Form
            </VBtn>
          </div>
        </VForm>
      </div>
    </VContainer>
  </VCard>
  <!--Form Status Complete-->
  <Card v-else-if="isFormCompleted">
    <div class="py-4 text-center">
      <v-icon color="success" size="48" class="my-2">mdi-alert-circle</v-icon>
      <h1>The short form was successfully submitted!</h1>
      <VDivider class="my-3" />
      <p class="my-4"><b>Sales Call 1 going well?</b></p>
      <p class="my-4"><b>If YES</b>, get easy info NOW BELOW</p>
      <p class="my-4"><b>If CLIENT STRESSED</b>, push to Sales Call 2</p>
      <p v-if="applicationId" class="my-4">
        Next Step 1 -
        <router-link
          :to="{ name: 'Agent Form Page', params: { id: applicationId }, query: { step: 'Summary' } }"
          target="_blank"
          ><b>GET LAST INFO now</b></router-link
        >
      </p>
    </div>
  </Card>
  <!--Form Not Found-->
  <Card v-else>
    <VCardItem class="pa-sm-8">
      <div class="py-4 text-center">
        <v-icon color="error" size="48" class="my-2">mdi-alert-circle</v-icon>
        <h1>The short not found!</h1>
        <p>Failed to load the sort form. Please check your link or try again later.</p>
      </div>
    </VCardItem>
  </Card>
</template>

<script>
import { mapActions } from 'vuex';
// Components
import { VDateInput } from 'vuetify/labs/VDateInput';
import Card from '@/components/common/Card/Card.vue';
import AddressSearch from '../AddressSearch/AddressSearch.vue';
// Service
import ApplicationService from '@/services/application-service/application.service';
import AddressService from '@/services/address-service/address.service';
import { toLocalISODate } from '@/utils/toLocalISODate';

export default {
  name: 'ShortForm',

  inject: ['countryListOptions'],

  props: {
    isAgentForm: {
      type: Boolean
    }
  },

  components: {
    VDateInput,
    Card,
    AddressSearch
  },

  mounted() {
    if (this.$route?.params?.id) {
      this.applicationId = this.$route.params.id;
      this.initForm();
    }
  },

  computed: {
    stateClientOptions() {
      const options = [{ title: 'Select County/State', value: '' }];
      const countryCode = this.formData.client.country?.toLowerCase();
      if (countryCode && this.countryListOptions[countryCode]?.states) {
        const states = this.countryListOptions[countryCode].states;
        for (const stateCode in states) {
          options.push({
            title: states[stateCode],
            value: states[stateCode]
          });
        }
      }
      return options;
    },
    hasStateClientOptions() {
      const countryCode = this.formData.client.country?.toLowerCase();
      return (
        countryCode &&
        this.countryListOptions[countryCode]?.states &&
        Object.keys(this.countryListOptions[countryCode].states).length > 0
      );
    },
    stateTrackBOptions() {
      const options = [{ title: 'Select County/State', value: '' }];
      const countryCode = this.formData.trackB.country?.toLowerCase();
      if (countryCode && this.countryListOptions[countryCode]?.states) {
        const states = this.countryListOptions[countryCode].states;
        for (const stateCode in states) {
          options.push({
            title: states[stateCode],
            value: states[stateCode]
          });
        }
      }
      return options;
    },
    hasStateTrackBOptions() {
      const countryCode = this.formData.trackB.country?.toLowerCase();
      return (
        countryCode &&
        this.countryListOptions[countryCode]?.states &&
        Object.keys(this.countryListOptions[countryCode].states).length > 0
      );
    },
    stateTrackCOptions() {
      const options = [{ title: 'Select County/State', value: '' }];
      const countryCode = this.formData.trackC.country?.toLowerCase();
      if (countryCode && this.countryListOptions[countryCode]?.states) {
        const states = this.countryListOptions[countryCode].states;
        for (const stateCode in states) {
          options.push({
            title: states[stateCode],
            value: states[stateCode]
          });
        }
      }
      return options;
    },
    hasStateTrackCOptions() {
      const countryCode = this.formData.trackC.country?.toLowerCase();
      return (
        countryCode &&
        this.countryListOptions[countryCode]?.states &&
        Object.keys(this.countryListOptions[countryCode].states).length > 0
      );
    },
    eligibilityOptions() {
      const salutation = this.formData.client.title;
      const maritalStatus = this.formData.client.maritalStatus;
      const livedInUK = this.formData.client.everLivedUK;

      // Map marital status to the broad categories from the image
      const isMarriedOrPartner = ['Married', 'Civil Partner', 'Separated', 'Divorced', 'Widowed'].includes(
        maritalStatus
      );
      const isSingle = maritalStatus === 'Single';

      // Determine available tracks based on salutation
      if (salutation === 'Mr') {
        if (isMarriedOrPartner) {
          if (livedInUK) {
            return {
              tracks: ['A', 'C'], // Self-Claim + Claim on Wife
              eligible: true
            };
          } else {
            return {
              tracks: ['C'], // Claim on Wife only
              eligible: true
            };
          }
        } else if (isSingle) {
          if (livedInUK) {
            return {
              tracks: ['A'], // Self-Claim only
              eligible: true
            };
          } else {
            return {
              tracks: [], // Nothing — not eligible
              eligible: false
            };
          }
        }
      } else if (['Mrs', 'Miss', 'Ms'].includes(salutation)) {
        if (isMarriedOrPartner) {
          if (livedInUK) {
            return {
              tracks: ['A', 'B'], // Self-Claim + Claim on Husband
              eligible: true
            };
          } else {
            return {
              tracks: ['B'], // Claim on Husband only
              eligible: true
            };
          }
        } else if (isSingle) {
          if (livedInUK) {
            return {
              tracks: ['A'], // Self-Claim only
              eligible: true
            };
          } else {
            return {
              tracks: [], // Nothing — not eligible
              eligible: false
            };
          }
        }
      } else if (salutation === 'Dr') {
        if (isMarriedOrPartner) {
          if (livedInUK) {
            return {
              tracks: ['A', 'B', 'C'], // All three
              eligible: true
            };
          } else {
            return {
              tracks: ['B', 'C'], // Claim on Husband + Claim on Wife
              eligible: true
            };
          }
        } else if (isSingle) {
          if (livedInUK) {
            return {
              tracks: ['A'], // Self-Claim only
              eligible: true
            };
          } else {
            return {
              tracks: [], // Nothing — not eligible
              eligible: false
            };
          }
        }
      }

      // Default - no salutation selected
      return {
        tracks: [],
        eligible: false
      };
    },
    maxDate() {
      const today = new Date();
      const minAgeDate = new Date(today.setFullYear(today.getFullYear() - 16));
      return toLocalISODate(minAgeDate);
    },
    dateToday() {
      const today = new Date();
      return toLocalISODate(today);
    },
    titleOptions() {
      return [
        { label: 'Choose', value: '' },
        { label: 'Mrs', value: 'Mrs' },
        { label: 'Mr', value: 'Mr' },
        { label: 'Miss', value: 'Miss' },
        { label: 'Ms', value: 'Ms' },
        { label: 'Dr', value: 'Dr' }
      ];
    },
    titleHusbandOptions() {
      return [
        { label: 'Choose', value: '' },
        { label: 'Mrs', value: 'Mrs' },
        { label: 'Mr', value: 'Mr' },
        { label: 'Dr', value: 'Dr' }
      ];
    },
    titleWifeOptions() {
      return [
        { label: 'Choose', value: '' },
        { label: 'Mr', value: 'Mr' },
        { label: 'Miss', value: 'Miss' },
        { label: 'Dr', value: 'Dr' }
      ];
    },
    maritalStatusOptions() {
      return [
        { label: 'Choose', value: '' },
        { label: 'Married', value: 'Married' },
        { label: 'Civil Partner', value: 'Civil Partner' },
        { label: 'Single', value: 'Single' },
        { label: 'Widowed', value: 'Widowed' },
        { label: 'Divorced', value: 'Divorced' },
        { label: 'Separated', value: 'Separated' }
      ];
    },
    approxYearsInUKptions() {
      const options = [];
      for (let i = 1; i < 31; i++) {
        options.push({
          label: i,
          value: i
        });
      }
      return options;
    },
    countryOptions() {
      const options = [{ label: 'Select Country', value: '' }];

      for (const countryCode in this.countryListOptions) {
        const country = this.countryListOptions[countryCode];
        options.push({
          label: country.name,
          value: countryCode.toUpperCase()
        });
      }

      return options;
    },
    countryUKOptions() {
      const options = [{ label: 'Select Country', value: '' }];
      options.push({
        label: 'United Kingdom',
        value: 'GBR'
      });

      return options;
    },
    isFormValid() {
      // Track must be selected
      if (!this.formData.track) return false;

      // Track A validation
      if (this.formData.track === 'A') {
        if (
          this.formData.trackA.isGettingUKStatePensionNow === null ||
          this.formData.trackA.isGettingUKStatePensionNow === undefined
        )
          return false;
        if (!this.formData.trackA.hasNino) return false;
        if (this.formData.trackA.hasNino === 'Yes' && !this.formData.trackA.nino) return false;

        // Check client address verification
        if (this.formData.client.addressValidated !== true) return false;

        // Validate trace data if NINO is Forgot
        if (this.formData.trackA.hasNino === 'Forgot' && !this.formData.trackA.isGettingUKStatePensionNow) {
          if (this.formData.trackA.employers.length === 0) return false;
          if (this.formData.trackA.addresses.length === 0) return false;

          for (const emp of this.formData.trackA.employers) {
            if (!emp.name || !emp.fromDate || !emp.toDate || !emp.postcode || !emp.address1 || !emp.city) return false;
            // if (emp.addressValidated !== true) return false;
          }
          for (const addr of this.formData.trackA.addresses) {
            if (!addr.postcode || !addr.address1 || !addr.city || !addr.fromDate || !addr.toDate) return false;
            // if (addr.addressValidated !== true) return false;
          }
        }
        return true;
      }

      // Track B validation
      if (this.formData.track === 'B') {
        if (this.formData.trackB.husbandAlive === null || this.formData.trackB.husbandAlive === undefined) return false;
        if (!this.formData.trackB.husbandFirstName) return false;
        if (!this.formData.trackB.title) return false;
        if (!this.formData.trackB.husbandSurname) return false;
        if (!this.formData.trackB.husbandDob) return false;
        if (!this.formData.trackB.marriageDate) return false;
        if (this.formData.trackB.sameAddress === null || this.formData.trackB.sameAddress === undefined) return false;

        // Check address verification
        if (!this.formData.trackB.sameAddress) {
          if (
            !this.formData.trackB.country ||
            !this.formData.trackB.postcode ||
            !this.formData.trackB.address1 ||
            !this.formData.trackB.city
          )
            return false;
          if (this.formData.trackB.addressValidated !== true) return false;
        } else if (this.formData.client.addressValidated !== true) {
          return false;
        }

        if (!this.formData.trackB.husbandHasNino) return false;
        if (this.formData.trackB.husbandHasNino === 'Yes' && !this.formData.trackB.husbandNino) return false;

        // Validate trace data if NINO is Forgot or Widow
        if (this.formData.trackB.husbandHasNino === 'Forgot' || this.formData.trackB.husbandAlive === false) {
          if (this.formData.trackB.employers.length === 0) return false;
          if (this.formData.trackB.addresses.length === 0) return false;

          for (const emp of this.formData.trackB.employers) {
            if (!emp.name || !emp.fromDate || !emp.toDate || !emp.postcode || !emp.address1 || !emp.city) return false;
            // if (emp.addressValidated !== true) return false;
          }
          for (const addr of this.formData.trackB.addresses) {
            if (!addr.postcode || !addr.address1 || !addr.city || !addr.fromDate || !addr.toDate) return false;
            // if (addr.addressValidated !== true) return false;
          }
          if (this.formData.trackB.husbandAlive === false && !this.formData.trackB.townsLived) return false;
        }
        return true;
      }

      // Track C validation
      if (this.formData.track === 'C') {
        if (!this.formData.trackC.wifeDob) return false;
        if (!this.formData.trackC.gatePassed) return false;
        if (!this.formData.trackC.marriageDate) return false;
        if (!this.formData.trackC.wifeLeftUK) return false;
        if (this.formData.trackC.sameAddress === null || this.formData.trackC.sameAddress === undefined) return false;

        // Check address verification
        if (!this.formData.trackC.sameAddress) {
          if (
            !this.formData.trackC.country ||
            !this.formData.trackC.postcode ||
            !this.formData.trackC.address1 ||
            !this.formData.trackC.city
          )
            return false;
          if (this.formData.trackC.addressValidated !== true) return false;
        } else if (this.formData.client.addressValidated !== true) {
          return false;
        }

        if (!this.formData.trackC.wifeHasNino) return false;
        if (this.formData.trackC.wifeHasNino === 'Yes' && !this.formData.trackC.wifeNino) return false;

        // Validate trace data if NINO is Forgot
        if (this.formData.trackC.wifeHasNino === 'Forgot') {
          if (this.formData.trackC.employers.length === 0) return false;
          if (this.formData.trackC.addresses.length === 0) return false;

          for (const emp of this.formData.trackC.employers) {
            if (!emp.name || !emp.fromDate || !emp.toDate || !emp.postcode || !emp.address1 || !emp.city) return false;
            // if (emp.addressValidated !== true) return false;
          }
          for (const addr of this.formData.trackC.addresses) {
            if (!addr.postcode || !addr.address1 || !addr.city || !addr.fromDate || !addr.toDate) return false;
            // if (addr.addressValidated !== true) return false;
          }
        }
        return true;
      }

      return false;
    }
  },
  beforeUnmount() {
    // Clear auto-verify debounce timer
    if (this.clientAddressDebounceTimer) {
      clearTimeout(this.clientAddressDebounceTimer);
    }
  },
  // ─── Data ──────────────────────────────────────────────
  data() {
    return {
      applicationId: '',

      valid: false,

      // UI state
      isLoadingInit: true,
      isLoadingSave: false,
      isFormPending: false,
      isFormCompleted: false,

      // Address validation state
      isValidatingAddress: false,
      clientAddressDebounceTimer: null,

      // ─── Validation Rules ────────────────────────────
      rules: {
        required: (v) => !!v || 'This field is required.',
        email: (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Enter a valid email address.',
        friendEmail: [
          (v) => {
            if (v?.length) {
              return /.+@.+\..+/.test(v) || 'E-mail must be valid';
            }
            return true;
          }
        ],
        phone: (v) => !v || /^[\d\s()+\\-]{7,20}$/.test(v) || 'Enter a valid phone number.',
        postcode: (v) => {
          if (!v) return true;
          const eircodeRegex = /^[A-Za-z0-9\s]{5,8}$/;
          return eircodeRegex.test(v) || 'Please enter a valid postcode';
        },
        nino: (v) =>
          !v ||
          /^(?!BG|GB|NK|KN|TN|NT|ZZ)[A-CEGHJ-PR-TW-Z]{2}[0-9]{6}[A-D]?$/.test((v || '').replace(/\s/g, '')) ||
          'Enter a valid NI number (e.g. NX123456B).'
      },

      // ─── Form Data ───────────────────────────────────
      formData: {
        // Client info
        client: {
          title: '',
          firstName: '',
          lastName: '',
          maidenName: '',
          dob: '',
          maritalStatus: '',
          nationality: '',
          dateOfDivorce: '',
          // Address
          country: '',
          postcode: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          // Verification status
          addressValidated: null,
          verificationStatus: null,
          wasCleansed: false,
          verifiedReference: null,
          verificationDetails: null,
          // Other
          tel: '',
          email: '',
          // pps: '',
          helperEmail: '',
          everLivedUK: false,
          dateLeftUK: '',
          approxYearsInUK: null
        },

        // Track selection: 'A', 'B', or 'C'
        track: null,

        // Track A: Own record
        trackA: {
          isGettingUKStatePensionNow: null,
          hasNino: null, // 'Yes', 'Forgot'
          nino: '',
          employers: [],
          addresses: []
        },

        // Track B: Husband's record
        trackB: {
          husbandAlive: null,
          husbandGettinStatePensionNow: null,
          youGettinStatePensionNow: null,
          title: '',
          husbandFirstName: '',
          husbandSurname: '',
          husbandOtherName: '',
          husbandDob: '',
          marriageDate: '',
          husbandLeftUK: '',
          sameAddress: true,
          // Address
          country: '',
          postcode: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          // Verification status
          addressValidated: null,
          verificationStatus: null,
          wasCleansed: false,
          verifiedReference: null,
          verificationDetails: null,
          husbandHasNino: null, // 'Yes', 'Forgot'
          husbandNino: '',
          employers: [],
          addresses: [],
          townsLived: ''
        },

        // Track C: Wife's record (60% rule)
        trackC: {
          wifeDob: '',
          gateResult: false,
          gatePassed: false,
          spaDate: '',
          marriageDate: '',
          wifeLeftUK: '',
          sameAddress: true,
          // Address
          country: '',
          postcode: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          // Verification status
          addressValidated: null,
          verificationStatus: null,
          wasCleansed: false,
          verifiedReference: null,
          verificationDetails: null,
          wifeHasNino: null, // 'Yes', 'Forgot'
          wifeNino: '',
          employers: [],
          addresses: []
        }
      }
    };
  },

  watch: {
    // ─── Client watchers ──────────────────────────────────
    'formData.client.title': {
      handler() {
        this.validateTrackEligibility();
      }
    },
    'formData.client.everLivedUK': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.formData.client.approxYearsInUK = null;
          this.formData.client.dateLeftUK = null;
        }
        this.validateTrackEligibility();
      }
    },
    'formData.client.maritalStatus': {
      handler(newVal, oldVal) {
        if (oldVal && newVal !== oldVal) {
          if (!['Divorced', 'Separated'].includes(newVal)) {
            this.formData.client.dateOfDivorce = '';
          }
          this.formData.track = null;
          this.track = null;
        }
        this.validateTrackEligibility();
      }
    },

    // ─── Track A watchers ─────────────────────────────────
    'formData.trackA.hasNino': {
      handler(newVal) {
        if (newVal === 'Yes') {
          this.formData.trackA.employers = [];
          this.formData.trackA.addresses = [];
        } else if (newVal === 'Forgot') {
          if (this.formData.trackA.employers.length === 0) {
            this.addEmployer('A');
          }
          if (this.formData.trackA.addresses.length === 0) {
            this.addAddress('A');
          }
        }
      }
    },

    // ─── Track B watchers ─────────────────────────────────
    'formData.trackB.husbandHasNino': {
      handler(newVal) {
        if (newVal === 'Forgot') {
          if (this.formData.trackB.employers.length === 0) {
            this.addEmployer('B');
          }
          if (this.formData.trackB.addresses.length === 0) {
            this.addAddress('B');
          }
        }
      }
    },

    'formData.trackB.husbandAlive': {
      handler(newVal) {
        if (newVal === false) {
          if (this.formData.trackB.employers.length === 0) {
            this.addEmployer('B');
          }
          if (this.formData.trackB.addresses.length === 0) {
            this.addAddress('B');
          }
        }
      }
    },

    'formData.trackB.sameAddress': {
      handler(newVal) {
        if (newVal) {
          this.copyClientAddressTo('trackB');
          this.copyClientVerificationTo('trackB');
        } else {
          this.clearPartnerAddress('trackB');
          this.clearPartnerVerification('trackB');
        }
      }
    },

    // ─── Track C watchers ─────────────────────────────────
    'formData.trackC.wifeHasNino': {
      handler(newVal) {
        if (newVal === 'Forgot') {
          if (this.formData.trackC.employers.length === 0) {
            this.addEmployer('C');
          }
          if (this.formData.trackC.addresses.length === 0) {
            this.addAddress('C');
          }
        }
      }
    },

    'formData.trackC.sameAddress': {
      handler(newVal) {
        if (newVal) {
          this.copyClientAddressTo('trackC');
          this.copyClientVerificationTo('trackC');
        } else {
          this.clearPartnerAddress('trackC');
          this.clearPartnerVerification('trackC');
        }
      }
    },

    // ─── Client address watchers for syncing ─────────────
    'formData.client.country': {
      handler() {
        if (this.formData.track === 'B' && this.formData.trackB.sameAddress) {
          this.formData.trackB.country = this.formData.client.country;
        }
        if (this.formData.track === 'C' && this.formData.trackC.sameAddress) {
          this.formData.trackC.country = this.formData.client.country;
        }
        this.debouncedVerifyClientAddress();
      }
    },
    'formData.client.postcode': {
      handler() {
        if (this.formData.track === 'B' && this.formData.trackB.sameAddress) {
          this.formData.trackB.postcode = this.formData.client.postcode;
        }
        if (this.formData.track === 'C' && this.formData.trackC.sameAddress) {
          this.formData.trackC.postcode = this.formData.client.postcode;
        }
        this.debouncedVerifyClientAddress();
      }
    },
    'formData.client.address1': {
      handler() {
        if (this.formData.track === 'B' && this.formData.trackB.sameAddress) {
          this.formData.trackB.address1 = this.formData.client.address1;
        }
        if (this.formData.track === 'C' && this.formData.trackC.sameAddress) {
          this.formData.trackC.address1 = this.formData.client.address1;
        }
        this.debouncedVerifyClientAddress();
      }
    },
    'formData.client.address2': {
      handler() {
        if (this.formData.track === 'B' && this.formData.trackB.sameAddress) {
          this.formData.trackB.address2 = this.formData.client.address2;
        }
        if (this.formData.track === 'C' && this.formData.trackC.sameAddress) {
          this.formData.trackC.address2 = this.formData.client.address2;
        }
        this.debouncedVerifyClientAddress();
      }
    },
    'formData.client.city': {
      handler() {
        if (this.formData.track === 'B' && this.formData.trackB.sameAddress) {
          this.formData.trackB.city = this.formData.client.city;
        }
        if (this.formData.track === 'C' && this.formData.trackC.sameAddress) {
          this.formData.trackC.city = this.formData.client.city;
        }
        this.debouncedVerifyClientAddress();
      }
    },
    'formData.client.state': {
      handler() {
        if (this.formData.track === 'B' && this.formData.trackB.sameAddress) {
          this.formData.trackB.state = this.formData.client.state;
        }
        if (this.formData.track === 'C' && this.formData.trackC.sameAddress) {
          this.formData.trackC.state = this.formData.client.state;
        }
        this.debouncedVerifyClientAddress();
      }
    },
    stateClientOptions: {
      handler(newVal) {
        if (!newVal) {
          this.formData.client.state = '';
        }
      }
    },
    stateTrackBOptions: {
      handler(newVal) {
        if (!newVal) {
          this.formData.trackB.state = '';
        }
      }
    },
    stateTrackCOptions: {
      handler(newVal) {
        if (!newVal) {
          this.formData.trackC.state = '';
        }
      }
    },
    // ─── Track change reset watcher ──────────────────────
    'formData.track': {
      handler(newTrack, oldTrack) {
        if (oldTrack && newTrack !== oldTrack) {
          this.resetTrackData(newTrack);
        }
      }
    }
  },

  // ─── Methods ───────────────────────────────────────────
  methods: {
    ...mapActions('main', ['showSnackbar']),

    async initForm() {
      this.isLoadingInit = true;
      try {
        const { application, status } = await ApplicationService.getApplicationShortDetails({
          applicationId: this.applicationId
        });
        console.log('Init Form:application', application);
        if (status === 'success') {
          this.isFormPending = true;
          await this.formatData(application ?? {});
          this.initAutoSave();
        } else {
          this.showSnackbar({
            text: 'Oops, Something Went Wrong!',
            color: 'error'
          });
        }
      } catch (err) {
        console.error(err);
        const { message } = err;
        // this.showSnackbar({
        //   text: message ?? 'Oops, Something Went Wrong!',
        //   color: 'error'
        // });
        if (message == 'The form has already been successfully submitted') {
          this.isFormCompleted = true;
        }
      } finally {
        this.isLoadingInit = false;
      }
    },

    validateTrackEligibility() {
      // If current track is not in available tracks, clear it
      if (this.formData.track && !this.eligibilityOptions.tracks.includes(this.formData.track)) {
        this.formData.track = null;
        this.track = null;
      }

      // If not eligible at all, clear track
      if (!this.eligibilityOptions.eligible) {
        this.formData.track = null;
        this.track = null;
      }
    },
    onClientAddressSelected(address) {
      const target = this.formData.client;

      if (address.address1 !== undefined) target.address1 = address.address1;
      if (address.address2 !== undefined) target.address2 = address.address2;
      if (address.city !== undefined) target.city = address.city;
      if (address.state !== undefined) target.state = address.state;
      if (address.postcode !== undefined) target.postcode = address.postcode;

      this.resetClientVerification();
      this.debouncedVerifyClientAddress();
    },
    onTrackBAddressSelected(address) {
      const target = this.formData.trackB;

      if (address.address1 !== undefined) target.address1 = address.address1;
      if (address.address2 !== undefined) target.address2 = address.address2;
      if (address.city !== undefined) target.city = address.city;
      if (address.state !== undefined) target.state = address.state;
      if (address.postcode !== undefined) target.postcode = address.postcode;

      this.resetPartnerVerification('trackB');
    },
    onTrackCAddressSelected(address) {
      const target = this.formData.trackC;

      if (address.address1 !== undefined) target.address1 = address.address1;
      if (address.address2 !== undefined) target.address2 = address.address2;
      if (address.city !== undefined) target.city = address.city;
      if (address.state !== undefined) target.state = address.state;
      if (address.postcode !== undefined) target.postcode = address.postcode;

      this.resetPartnerVerification('trackC');
    },
    // ─── Reset verification methods ──────────────────
    resetClientVerification() {
      if (this.isLoadingSave || this.isLoadingInit) {
        return;
      }
      this.formData.client.addressValidated = null;
      this.formData.client.verificationStatus = null;
      this.formData.client.wasCleansed = false;
      this.formData.client.verifiedReference = null;
      this.formData.client.verificationDetails = null;
    },

    resetPartnerVerification(track) {
      if (this.isLoadingSave || this.isLoadingInit) {
        return;
      }
      const target = track === 'trackB' ? this.formData.trackB : this.formData.trackC;
      target.addressValidated = null;
      target.verificationStatus = null;
      target.wasCleansed = false;
      target.verifiedReference = null;
      target.verificationDetails = null;
    },

    resetEmployerVerification(track, index) {
      if (this.isLoadingSave || this.isLoadingInit) {
        return;
      }
      const key = `track${track}`;
      const employer = this.formData[key].employers[index];
      if (employer) {
        employer.addressValidated = null;
        employer.verificationStatus = null;
        employer.wasCleansed = false;
        employer.verifiedReference = null;
        employer.verificationDetails = null;
      }
    },

    resetPastAddressVerification(track, index) {
      if (this.isLoadingSave || this.isLoadingInit) {
        return;
      }
      const key = `track${track}`;
      const address = this.formData[key].addresses[index];
      if (address) {
        address.addressValidated = null;
        address.verificationStatus = null;
        address.wasCleansed = false;
        address.verifiedReference = null;
        address.verificationDetails = null;
      }
    },

    // ─── Address verification helpers ────────────────────
    getVerificationStatus(addressObj) {
      if (!addressObj) return 'Not verified';
      if (addressObj.addressValidated === null || addressObj.addressValidated === undefined) return 'Not verified';
      if (addressObj.addressValidated === true) {
        if (addressObj.verificationStatus === 'Partial') return 'Partial';
        if (addressObj.verificationStatus === 'Invalid') return 'Invalid';
        return 'Verified ✓';
      }
      if (addressObj.addressValidated === false) return 'Invalid ✗';
      return 'Not verified';
    },
    getVerificationIcon(addressObj) {
      if (!addressObj) return 'mdi-help-circle';
      if (addressObj.addressValidated === null || addressObj.addressValidated === undefined) return 'mdi-help-circle';
      if (addressObj.addressValidated === true) {
        if (addressObj.verificationStatus === 'Partial') return 'mdi-alert-circle';
        if (addressObj.verificationStatus === 'Invalid') return 'mdi-close-circle';
        return 'mdi-check-circle';
      }
      if (addressObj.addressValidated === false) return 'mdi-close-circle';
      return 'mdi-help-circle';
    },
    getVerificationColor(addressObj) {
      if (!addressObj) return 'grey';
      if (addressObj.addressValidated === null || addressObj.addressValidated === undefined) return 'grey';
      if (addressObj.addressValidated === true) {
        if (addressObj.verificationStatus === 'Partial') return 'warning';
        if (addressObj.verificationStatus === 'Invalid') return 'error';
        return 'success';
      }
      if (addressObj.addressValidated === false) return 'error';
      return 'grey';
    },
    // ─── Address Helper Methods ──────────────────────────
    copyClientAddressTo(track) {
      const target = track === 'trackB' ? this.formData.trackB : this.formData.trackC;
      target.country = this.formData.client.country;
      target.postcode = this.formData.client.postcode;
      target.address1 = this.formData.client.address1;
      target.address2 = this.formData.client.address2;
      target.city = this.formData.client.city;
      target.state = this.formData.client.state;
    },

    copyClientVerificationTo(track) {
      const target = track === 'trackB' ? this.formData.trackB : this.formData.trackC;
      target.addressValidated = this.formData.client.addressValidated;
      target.verificationStatus = this.formData.client.verificationStatus;
      target.wasCleansed = this.formData.client.wasCleansed;
      target.verifiedReference = this.formData.client.verifiedReference;
      target.verificationDetails = this.formData.client.verificationDetails;
    },

    clearPartnerAddress(track) {
      const target = track === 'trackB' ? this.formData.trackB : this.formData.trackC;
      target.country = '';
      target.postcode = '';
      target.address1 = '';
      target.address2 = '';
      target.city = '';
      target.state = '';
    },

    clearPartnerVerification(track) {
      const target = track === 'trackB' ? this.formData.trackB : this.formData.trackC;
      target.addressValidated = null;
      target.verificationStatus = null;
      target.wasCleansed = false;
      target.verifiedReference = null;
      target.verificationDetails = null;
    },

    // ─── Address Verification Methods ──────────────────
    debouncedVerifyClientAddress() {
      // Don't auto-verify during initial load or save
      if (this.isLoadingInit || this.isLoadingSave) {
        return;
      }

      if (this.clientAddressDebounceTimer) {
        clearTimeout(this.clientAddressDebounceTimer);
      }

      this.clientAddressDebounceTimer = setTimeout(() => {
        this.autoVerifyClientAddress();
      }, 1500);
    },

    autoVerifyClientAddress() {
      const c = this.formData.client;

      // Only auto-verify if all required address fields are filled
      if (!c.address1 || !c.city || !c.country) {
        return;
      }

      // Don't re-verify if already successfully verified
      if (c.addressValidated === true && c.verificationStatus !== 'Invalid') {
        return;
      }

      // Don't verify if already in progress
      if (this.isValidatingAddress) {
        return;
      }

      this.verifyClientAddress(true);
    },
    async verifyAddressWithLoqate(addressObj, context) {
      if (!addressObj || !addressObj.address1 || !addressObj.city || !addressObj.country) {
        this.showSnackbar({
          text: 'Please fill in address fields before verification.',
          color: 'warning'
        });
        return;
      }

      this.isValidatingAddress = true;

      try {
        const result = await AddressService.autoVerifyAddress(addressObj, addressObj.country || 'GBR');

        if (result.success) {
          // Set only the two main fields
          addressObj.addressValidated = result.isValid;
          addressObj.verificationStatus = result.verificationStatus; // 'Verified', 'Partial', 'Invalid', 'Not verified'
          addressObj.wasCleansed = result.wasCleansed;
          addressObj.verifiedReference = result.verificationDetails?.verifiedAddressReference || null;
          addressObj.verificationDetails = {
            avc: result.avc,
            aqi: result.aqi,
            matchScore: result.matchScore,
            postProcessedLevel: result.postProcessedLevel,
            preProcessedLevel: result.preProcessedLevel
          };

          if (context === 'client') {
            if (this.formData.track === 'B' && this.formData.trackB.sameAddress) {
              this.copyClientVerificationTo('trackB');
            }
            if (this.formData.track === 'C' && this.formData.trackC.sameAddress) {
              this.copyClientVerificationTo('trackC');
            }
          }

          this.showSnackbar({
            text: result.message + (result.isValid ? ' ✅' : ' ⚠️'),
            color: result.isValid ? 'success' : 'warning'
          });
        } else {
          addressObj.addressValidated = false;
          addressObj.verificationStatus = 'Invalid';
          addressObj.wasCleansed = false;
          addressObj.verifiedReference = null;
          addressObj.verificationDetails = null;

          this.showSnackbar({
            text: result.message || 'Address verification failed',
            color: 'error'
          });
        }
      } catch (err) {
        console.error('Address verification error:', err);
        addressObj.addressValidated = false;
        addressObj.verificationStatus = 'Invalid';
        addressObj.wasCleansed = false;
        addressObj.verifiedReference = null;
        addressObj.verificationDetails = null;
        this.showSnackbar({
          text: 'Failed to verify address. Please try again.',
          color: 'error'
        });
      } finally {
        this.isValidatingAddress = false;
      }
    },

    verifyClientAddress(isAuto = false) {
      // Prevent duplicate calls
      if (this.isValidatingAddress) {
        return;
      }
      this.verifyAddressWithLoqate(this.formData.client, 'client', isAuto);
    },

    verifyPartnerBAddress() {
      if (!this.formData.trackB.sameAddress) {
        this.verifyAddressWithLoqate(this.formData.trackB, 'partnerB');
      } else {
        this.showSnackbar({
          text: 'Address is same as claimant. Verify claimant address instead.',
          color: 'info'
        });
      }
    },

    verifyPartnerCAddress() {
      if (!this.formData.trackC.sameAddress) {
        this.verifyAddressWithLoqate(this.formData.trackC, 'partnerC');
      } else {
        this.showSnackbar({
          text: 'Address is same as claimant. Verify claimant address instead.',
          color: 'info'
        });
      }
    },

    // verifyEmployerAddress(track, index) {
    //   const key = `track${track}`;
    //   const employer = this.formData[key].employers[index];
    //   if (employer) {
    //     this.verifyAddressWithLoqate(employer, `employer-${track}-${index}`);
    //   }
    // },

    // verifyPastAddress(track, index) {
    //   const key = `track${track}`;
    //   const address = this.formData[key].addresses[index];
    //   if (address) {
    //     this.verifyAddressWithLoqate(address, `address-${track}-${index}`);
    //   }
    // },

    formatData(application = {}) {
      const { client, track, trackA, trackB, trackC } = application;

      // Format client data
      if (client) {
        this.formData.client = {
          title: client?.title ?? '',
          firstName: client?.firstName ?? '',
          middleName: client?.middleName ?? '',
          lastName: client?.lastName ?? '',
          maidenName: client?.maidenName ?? '',
          dob: client?.dob ?? '',
          maritalStatus: client?.maritalStatus ?? '',
          nationality: client?.nationality ?? '',
          dateOfDivorce: client?.dateOfDivorce ?? '',
          // Address
          country: client?.country ?? '',
          postcode: client?.postcode ?? '',
          address1: client?.address1 ?? '',
          address2: client?.address2 ?? '',
          city: client?.city ?? '',
          state: client?.stateName ?? '',
          // Verification status
          addressValidated: client?.addressValidated ?? null,
          verificationStatus: client?.verificationStatus ?? null,
          wasCleansed: client?.wasCleansed ?? false,
          verifiedReference: client?.verifiedReference ?? null,
          verificationDetails: client?.verificationDetails ?? null,
          // Info
          tel: client?.tel ?? '',
          email: client?.email ?? '',
          // pps: client?.pps ?? '',
          helperEmail: client?.helperEmail ?? '',
          everLivedUK: client?.everLivedUK ?? false,
          dateLeftUK: client?.dateLeftUK ?? '',
          approxYearsInUK: client?.approxYearsInUK ?? null
        };
      }

      // Set track
      if (track) {
        this.formData.track = track;
      }

      // Format Track A data
      if (trackA) {
        this.formData.trackA = {
          isGettingUKStatePensionNow: trackA?.isGettingUKStatePensionNow ?? null,
          hasNino: trackA?.hasNino ?? null,
          nino: trackA?.nino ?? '',
          employers: this.formatEmployers(trackA?.employers ?? []),
          addresses: this.formatAddresses(trackA?.addresses ?? [])
        };
      } else if (this.formData.track === 'A') {
        this.formData.trackA = {
          isGettingUKStatePensionNow: null,
          hasNino: null,
          nino: '',
          employers: [],
          addresses: []
        };
      }

      // Format Track B data
      if (trackB) {
        this.formData.trackB = {
          husbandAlive: trackB?.husbandAlive ?? null,
          husbandGettinStatePensionNow: trackB?.husbandGettinStatePensionNow ?? null,
          youGettinStatePensionNow: trackB?.youGettinStatePensionNow ?? null,
          title: trackB?.title ?? '',
          husbandFirstName: trackB?.husbandFirstName ?? '',
          husbandSurname: trackB?.husbandSurname ?? '',
          husbandOtherName: trackB?.husbandOtherName ?? '',
          husbandDob: trackB?.husbandDob ?? '',
          marriageDate: trackB?.marriageDate ?? '',
          husbandLeftUK: trackB?.husbandLeftUK ?? '',
          sameAddress: trackB?.sameAddress ?? true,
          // Address
          country: trackB?.country ?? '',
          postcode: trackB?.postcode ?? '',
          address1: trackB?.address1 ?? '',
          address2: trackB?.address2 ?? '',
          city: trackB?.city ?? '',
          state: trackB?.stateName ?? '',
          // Verification status
          addressValidated: trackB?.addressValidated ?? null,
          verificationStatus: trackB?.verificationStatus ?? null,
          wasCleansed: trackB?.wasCleansed ?? false,
          verifiedReference: trackB?.verifiedReference ?? null,
          verificationDetails: trackB?.verificationDetails ?? null,
          husbandHasNino: trackB?.husbandHasNino ?? null,
          husbandNino: trackB?.husbandNino ?? '',
          employers: this.formatEmployers(trackB?.employers ?? []),
          addresses: this.formatAddresses(trackB?.addresses ?? []),
          townsLived: trackB?.townsLived ?? ''
        };
      } else if (this.formData.track === 'B') {
        this.formData.trackB = {
          husbandAlive: null,
          husbandGettinStatePensionNow: null,
          youGettinStatePensionNow: null,
          title: '',
          husbandFirstName: '',
          husbandSurname: '',
          husbandOtherName: '',
          husbandDob: '',
          marriageDate: '',
          husbandLeftUK: '',
          sameAddress: true,
          country: '',
          postcode: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          addressValidated: null,
          verificationStatus: null,
          wasCleansed: false,
          verifiedReference: null,
          verificationDetails: null,
          husbandHasNino: null,
          husbandNino: '',
          employers: [],
          addresses: [],
          townsLived: ''
        };
      }

      // Format Track C data
      if (trackC) {
        this.formData.trackC = {
          wifeDob: trackC?.wifeDob ?? '',
          gateResult: trackC?.gateResult ?? false,
          gatePassed: trackC?.gatePassed ?? false,
          spaDate: trackC?.spaDate ?? '',
          marriageDate: trackC?.marriageDate ?? '',
          wifeLeftUK: trackC?.wifeLeftUK ?? '',
          sameAddress: trackC?.sameAddress ?? true,
          // Address
          country: trackC?.country ?? '',
          postcode: trackC?.postcode ?? '',
          address1: trackC?.address1 ?? '',
          address2: trackC?.address2 ?? '',
          city: trackC?.city ?? '',
          state: trackC?.stateName ?? '',
          // Verification status
          addressValidated: trackC?.addressValidated ?? null,
          verificationStatus: trackC?.verificationStatus ?? null,
          wasCleansed: trackC?.wasCleansed ?? false,
          verifiedReference: trackC?.verifiedReference ?? null,
          verificationDetails: trackC?.verificationDetails ?? null,
          wifeHasNino: trackC?.wifeHasNino ?? null,
          wifeNino: trackC?.wifeNino ?? '',
          employers: this.formatEmployers(trackC?.employers ?? []),
          addresses: this.formatAddresses(trackC?.addresses ?? [])
        };
      } else if (this.formData.track === 'C') {
        this.formData.trackC = {
          wifeDob: '',
          gateResult: false,
          gatePassed: false,
          spaDate: '',
          marriageDate: '',
          wifeLeftUK: '',
          sameAddress: true,
          country: '',
          postcode: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          addressValidated: null,
          verificationStatus: null,
          wasCleansed: false,
          verifiedReference: null,
          verificationDetails: null,
          wifeHasNino: null,
          wifeNino: '',
          employers: [],
          addresses: []
        };
      }

      // If track is set but track data is empty, initialize default data
      if (this.formData.track === 'A' && this.formData.trackA.employers.length === 0) {
        this.addEmployer('A');
        this.addAddress('A');
      }
      if (
        this.formData.track === 'B' &&
        this.formData.trackB.employers.length === 0 &&
        this.formData.trackB.husbandHasNino === 'Forgot'
      ) {
        this.addEmployer('B');
        this.addAddress('B');
      }
      if (
        this.formData.track === 'C' &&
        this.formData.trackC.employers.length === 0 &&
        this.formData.trackC.wifeHasNino === 'Forgot'
      ) {
        this.addEmployer('C');
        this.addAddress('C');
      }

      // Sync partner address if sameAddress is true
      if (this.formData.track === 'B' && this.formData.trackB.sameAddress) {
        this.copyClientAddressTo('trackB');
        this.copyClientVerificationTo('trackB');
      }
      if (this.formData.track === 'C' && this.formData.trackC.sameAddress) {
        this.copyClientAddressTo('trackC');
        this.copyClientVerificationTo('trackC');
      }
    },

    // ── Array formatting helpers ──────────────────────────
    formatAddresses(addresses = []) {
      if (!Array.isArray(addresses)) return [];

      if (addresses.length === 0) {
        return [];
      }

      return addresses.map((address) => ({
        country: address?.country ?? 'GBR',
        postcode: address?.postcode ?? '',
        address1: address?.address1 ?? '',
        address2: address?.address2 ?? '',
        city: address?.city ?? '',
        fromDate: address?.fromDate ?? '',
        toDate: address?.toDate ?? '',
        // Verification status
        addressValidated: address?.addressValidated ?? null,
        verificationStatus: address?.verificationStatus ?? null,
        wasCleansed: address?.wasCleansed ?? false,
        verifiedReference: address?.verifiedReference ?? null,
        verificationDetails: address?.verificationDetails ?? null
      }));
    },

    formatEmployers(employers = []) {
      if (!Array.isArray(employers)) return [];

      if (employers.length === 0) {
        return [];
      }

      return employers.map((company) => ({
        name: company?.name ?? '',
        country: company?.country ?? 'GBR',
        postcode: company?.postcode ?? '',
        address1: company?.address1 ?? '',
        address2: company?.address2 ?? '',
        city: company?.city ?? '',
        fromDate: company?.fromDate ?? '',
        toDate: company?.toDate ?? '',
        // Verification status
        addressValidated: company?.addressValidated ?? null,
        verificationStatus: company?.verificationStatus ?? null,
        wasCleansed: company?.wasCleansed ?? false,
        verifiedReference: company?.verifiedReference ?? null,
        verificationDetails: company?.verificationDetails ?? null
      }));
    },

    /**
     * Reset all track data when track changes
     * @param {string} newTrack - The new track to set up
     */
    resetTrackData(newTrack) {
      // Clear Track A data
      this.formData.trackA = {
        isGettingUKStatePensionNow: null,
        hasNino: null,
        nino: '',
        employers: [],
        addresses: []
      };

      // Clear Track B data
      this.formData.trackB = {
        husbandAlive: null,
        husbandGettinStatePensionNow: null,
        youGettinStatePensionNow: null,
        title: '',
        husbandFirstName: '',
        husbandSurname: '',
        husbandOtherName: '',
        husbandDob: '',
        marriageDate: '',
        husbandLeftUK: '',
        sameAddress: true,
        country: '',
        postcode: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        addressValidated: null,
        verificationStatus: null,
        wasCleansed: false,
        verifiedReference: null,
        verificationDetails: null,
        husbandHasNino: null,
        husbandNino: '',
        employers: [],
        addresses: [],
        townsLived: ''
      };

      // Clear Track C data
      this.formData.trackC = {
        wifeDob: '',
        gateResult: false,
        gatePassed: false,
        spaDate: '',
        marriageDate: '',
        wifeLeftUK: '',
        sameAddress: true,
        country: '',
        postcode: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        addressValidated: null,
        verificationStatus: null,
        wasCleansed: false,
        verifiedReference: null,
        verificationDetails: null,
        wifeHasNino: null,
        wifeNino: '',
        employers: [],
        addresses: []
      };

      // Initialize default data for the selected track
      if (newTrack === 'A') {
        this.addEmployer('A');
        this.addAddress('A');
      }
    },

    selectTrack(track) {
      if (this.formData.track === track) return;
      this.formData.track = track;
    },

    // Track methods
    addEmployer(track) {
      const key = `track${track}`;
      this.formData[key].employers.push({
        name: '',
        country: 'GBR',
        postcode: '',
        address1: '',
        address2: '',
        city: '',
        fromDate: '',
        toDate: '',
        addressValidated: null,
        verificationStatus: null,
        wasCleansed: false,
        verifiedReference: null,
        verificationDetails: null
      });
    },

    removeEmployer(track, index) {
      const key = `track${track}`;
      this.formData[key].employers.splice(index, 1);
    },

    addAddress(track) {
      const key = `track${track}`;
      this.formData[key].addresses.push({
        country: 'GBR',
        postcode: '',
        address1: '',
        address2: '',
        city: '',
        fromDate: '',
        toDate: '',
        addressValidated: null,
        verificationStatus: null,
        wasCleansed: false,
        verifiedReference: null,
        verificationDetails: null
      });
    },

    removeAddress(track, index) {
      const key = `track${track}`;
      this.formData[key].addresses.splice(index, 1);
    },

    // Gate check for Track C
    checkGateC() {
      const dobStr = this.formData.trackC.wifeDob;
      if (!dobStr) {
        this.formData.trackC.gateResult = false;
        this.formData.trackC.gatePassed = false;
        this.formData.trackC.spaDate = '';
        return;
      }

      const dob = this.parseDob(dobStr);
      if (!dob || dob.getFullYear() < 1900 || dob > new Date()) {
        this.formData.trackC.gateResult = true;
        this.formData.trackC.gatePassed = false;
        this.formData.trackC.spaDate = 'Invalid date';
        return;
      }
      const spa = this.calculateWifeSPA(dob);

      if (!spa) {
        this.formData.trackC.gateResult = true;
        this.formData.trackC.gatePassed = false;
        this.formData.trackC.spaDate = 'Unknown';
        return;
      }

      const gateStart = new Date('2010-04-06');
      const gateEnd = new Date('2016-04-05');

      const spaFormatted = spa.toLocaleDateString('en-GB');

      this.formData.trackC.spaDate = spaFormatted;
      this.formData.trackC.gateResult = true;
      this.formData.trackC.gatePassed = spa >= gateStart && spa <= gateEnd;
    },
    parseDob(str) {
      if (!str) return null;

      // Уже Date
      if (str instanceof Date) {
        return isNaN(str) ? null : new Date(str.getFullYear(), str.getMonth(), str.getDate());
      }

      const s = String(str).trim();

      // dd/mm/yyyy или dd-mm-yyyy
      // eslint-disable-next-line no-useless-escape
      const dmy = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
      if (dmy) {
        const day = +dmy[1],
          month = +dmy[2],
          year = +dmy[3];
        const dt = new Date(year, month - 1, day);
        if (dt.getFullYear() === year && dt.getMonth() === month - 1 && dt.getDate() === day) {
          return dt;
        }
        return null;
      }

      // ISO: yyyy-mm-dd (опционально с временем)
      const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) {
        const year = +iso[1],
          month = +iso[2],
          day = +iso[3];
        return new Date(year, month - 1, day);
      }

      // Fallback — пусть Date попробует сам, но нормализуем к локальной полуночи
      const dt = new Date(s);
      if (isNaN(dt)) return null;
      return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    },
    /**
     * Calculate a woman's State Pension Age based on the PDF timetable.
     * Returns a Date object, or null if outside the supported range.
     */
    calculateWifeSPA(dob) {
      // Helper: build a Date from year, month (1-12), day
      const d = (y, m, day) => new Date(y, m - 1, day);

      // Born before 6 Apr 1950: SPA is the 60th birthday
      if (dob < d(1950, 4, 6)) {
        return d(dob.getFullYear() + 60, dob.getMonth() + 1, dob.getDate());
      }

      // Table 1: 6 Apr 1950 – 5 Apr 1953 (SPA 60 → 65)
      if (dob >= d(1950, 4, 6) && dob <= d(1953, 4, 5)) {
        // Table 1 rows (birth start → SPA date):
        const table1 = [
          { start: d(1950, 4, 6), end: d(1950, 5, 5), spa: d(2010, 5, 6) },
          { start: d(1950, 5, 6), end: d(1950, 6, 5), spa: d(2010, 7, 6) },
          { start: d(1950, 6, 6), end: d(1950, 7, 5), spa: d(2010, 9, 6) },
          { start: d(1950, 7, 6), end: d(1950, 8, 5), spa: d(2010, 11, 6) },
          { start: d(1950, 8, 6), end: d(1950, 9, 5), spa: d(2011, 1, 6) },
          { start: d(1950, 9, 6), end: d(1950, 10, 5), spa: d(2011, 3, 6) },
          { start: d(1950, 10, 6), end: d(1950, 11, 5), spa: d(2011, 5, 6) },
          { start: d(1950, 11, 6), end: d(1950, 12, 5), spa: d(2011, 7, 6) },
          { start: d(1950, 12, 6), end: d(1951, 1, 5), spa: d(2011, 9, 6) },
          { start: d(1951, 1, 6), end: d(1951, 2, 5), spa: d(2011, 11, 6) },
          { start: d(1951, 2, 6), end: d(1951, 3, 5), spa: d(2012, 1, 6) },
          { start: d(1951, 3, 6), end: d(1951, 4, 5), spa: d(2012, 3, 6) },
          { start: d(1951, 4, 6), end: d(1951, 5, 5), spa: d(2012, 5, 6) },
          { start: d(1951, 5, 6), end: d(1951, 6, 5), spa: d(2012, 7, 6) },
          { start: d(1951, 6, 6), end: d(1951, 7, 5), spa: d(2012, 9, 6) },
          { start: d(1951, 7, 6), end: d(1951, 8, 5), spa: d(2012, 11, 6) },
          { start: d(1951, 8, 6), end: d(1951, 9, 5), spa: d(2013, 1, 6) },
          { start: d(1951, 9, 6), end: d(1951, 10, 5), spa: d(2013, 3, 6) },
          { start: d(1951, 10, 6), end: d(1951, 11, 5), spa: d(2013, 5, 6) },
          { start: d(1951, 11, 6), end: d(1951, 12, 5), spa: d(2013, 7, 6) },
          { start: d(1951, 12, 6), end: d(1952, 1, 5), spa: d(2013, 9, 6) },
          { start: d(1952, 1, 6), end: d(1952, 2, 5), spa: d(2013, 11, 6) },
          { start: d(1952, 2, 6), end: d(1952, 3, 5), spa: d(2014, 1, 6) },
          { start: d(1952, 3, 6), end: d(1952, 4, 5), spa: d(2014, 3, 6) },
          { start: d(1952, 4, 6), end: d(1952, 5, 5), spa: d(2014, 5, 6) },
          { start: d(1952, 5, 6), end: d(1952, 6, 5), spa: d(2014, 7, 6) },
          { start: d(1952, 6, 6), end: d(1952, 7, 5), spa: d(2014, 9, 6) },
          { start: d(1952, 7, 6), end: d(1952, 8, 5), spa: d(2014, 11, 6) },
          { start: d(1952, 8, 6), end: d(1952, 9, 5), spa: d(2015, 1, 6) },
          { start: d(1952, 9, 6), end: d(1952, 10, 5), spa: d(2015, 3, 6) },
          { start: d(1952, 10, 6), end: d(1952, 11, 5), spa: d(2015, 5, 6) },
          { start: d(1952, 11, 6), end: d(1952, 12, 5), spa: d(2015, 7, 6) },
          { start: d(1952, 12, 6), end: d(1953, 1, 5), spa: d(2015, 9, 6) },
          { start: d(1953, 1, 6), end: d(1953, 2, 5), spa: d(2015, 11, 6) },
          { start: d(1953, 2, 6), end: d(1953, 3, 5), spa: d(2016, 1, 6) },
          { start: d(1953, 3, 6), end: d(1953, 4, 5), spa: d(2016, 3, 6) }
        ];

        for (const row of table1) {
          if (dob >= row.start && dob <= row.end) {
            return row.spa;
          }
        }
      }

      // Table 2: 6 Apr 1953 – 5 Dec 1953 (SPA 65 → 66)
      if (dob >= d(1953, 4, 6) && dob <= d(1953, 12, 5)) {
        const table2 = [
          { start: d(1953, 4, 6), end: d(1953, 5, 5), spa: d(2016, 7, 6) },
          { start: d(1953, 5, 6), end: d(1953, 6, 5), spa: d(2016, 11, 6) },
          { start: d(1953, 6, 6), end: d(1953, 7, 5), spa: d(2017, 3, 6) },
          { start: d(1953, 7, 6), end: d(1953, 8, 5), spa: d(2017, 7, 6) },
          { start: d(1953, 8, 6), end: d(1953, 9, 5), spa: d(2017, 11, 6) },
          { start: d(1953, 9, 6), end: d(1953, 10, 5), spa: d(2018, 3, 6) },
          { start: d(1953, 10, 6), end: d(1953, 11, 5), spa: d(2018, 7, 6) },
          { start: d(1953, 11, 6), end: d(1953, 12, 5), spa: d(2018, 11, 6) }
        ];

        for (const row of table2) {
          if (dob >= row.start && dob <= row.end) {
            return row.spa;
          }
        }
      }

      // For birth dates outside the tables above, the SPA is ≥ 66 (Table 3+) —
      // all such SPAs are after 5 Apr 2016, so they fail the gate.
      // Return null to indicate "outside supported range for this gate".
      return null;
    },

    // ── Persistence ───────────────────────────────────
    async saveStepData() {
      let isSaved = false;
      this.isLoadingSave = true;
      try {
        await ApplicationService.updateApplicationShortDetails({
          applicationId: this.applicationId,
          application: this.formData
        });
        isSaved = true;
      } catch (err) {
        console.error(err);
        const { message } = err;
        this.showSnackbar({
          text: message ?? 'Oops, Something Went Wrong!',
          color: 'error'
        });
      } finally {
        this.isLoadingSave = false;
      }
      return isSaved;
    },
    // ── Submit ────────────────────────────────────────
    async submitForm() {
      const formRef = this.$refs.form;
      if (formRef) {
        const { valid: isValid } = await formRef.validate();
        if (!isValid) {
          this.showSnackbar({
            text: 'Please fill in all required fields.',
            color: 'error'
          });
          return;
        }
      }
      if (!this.isFormValid) {
        this.showSnackbar({
          text: this.getValidationErrorMessage(),
          color: 'error'
        });
        return;
      }

      const isSaved = await this.saveStepData();

      if (isSaved) {
        // this.showSnackbar({ text: 'Big Envelope dispatched successfully!', color: 'success' });
        this.isFormPending = false;
        this.isFormCompleted = true;
        console.log('✓ Big Envelope dispatched.');
      }
    },

    getValidationErrorMessage() {
      // Track must be selected
      if (!this.formData.track) {
        return 'Please select a track (Self-Claim, Claim on Husband, or Claim on Wife).';
      }

      // Track A validation
      if (this.formData.track === 'A') {
        if (
          this.formData.trackA.isGettingUKStatePensionNow === null ||
          this.formData.trackA.isGettingUKStatePensionNow === undefined
        ) {
          return 'Please indicate if you are getting a UK State Pension now.';
        }
        if (!this.formData.trackA.hasNino) {
          return 'Please indicate if you have your UK National Insurance Number.';
        }
        if (this.formData.trackA.hasNino === 'Yes' && !this.formData.trackA.nino) {
          return 'Please enter your UK National Insurance Number.';
        }

        // Check client address verification
        if (this.formData.client.addressValidated !== true) {
          return 'Please verify your address using the "Verify Address" button.';
        }

        // Validate trace data if NINO is Forgot
        if (this.formData.trackA.hasNino === 'Forgot' && !this.formData.trackA.isGettingUKStatePensionNow) {
          if (this.formData.trackA.employers.length === 0) {
            return 'Please add at least one UK employer.';
          }
          if (this.formData.trackA.addresses.length === 0) {
            return 'Please add at least one past UK address.';
          }

          for (let i = 0; i < this.formData.trackA.employers.length; i++) {
            const emp = this.formData.trackA.employers[i];
            if (!emp.name) {
              return `Please enter employer name for Employer ${i + 1}.`;
            }
            if (!emp.fromDate) {
              return `Please enter start date for Employer ${i + 1}.`;
            }
            if (!emp.toDate) {
              return `Please enter end date for Employer ${i + 1}.`;
            }
            if (emp.toDate < emp.fromDate) {
              return `End date must be after start date for Employer ${i + 1}.`;
            }
            if (!emp.postcode) {
              return `Please enter postcode for Employer ${i + 1}.`;
            }
            if (!emp.address1) {
              return `Please enter address for Employer ${i + 1}.`;
            }
            if (!emp.city) {
              return `Please enter city for Employer ${i + 1}.`;
            }
            // Uncomment if address verification is required
            // if (emp.addressValidated !== true) {
            //   return `Please verify address for Employer ${i + 1}.`;
            // }
          }
          for (let i = 0; i < this.formData.trackA.addresses.length; i++) {
            const addr = this.formData.trackA.addresses[i];
            if (!addr.postcode) {
              return `Please enter postcode for Past Address ${i + 1}.`;
            }
            if (!addr.address1) {
              return `Please enter address for Past Address ${i + 1}.`;
            }
            if (!addr.city) {
              return `Please enter city for Past Address ${i + 1}.`;
            }
            if (!addr.fromDate) {
              return `Please enter start date for Past Address ${i + 1}.`;
            }
            if (!addr.toDate) {
              return `Please enter end date for Past Address ${i + 1}.`;
            }
            if (addr.toDate < addr.fromDate) {
              return `End date must be after start date for Past Address ${i + 1}.`;
            }
            // Uncomment if address verification is required
            // if (addr.addressValidated !== true) {
            //   return `Please verify address for Past Address ${i + 1}.`;
            // }
          }
        }
        return 'Please complete all required fields for the Self-Claim track.';
      }

      // Track B validation
      if (this.formData.track === 'B') {
        if (this.formData.trackB.husbandAlive === null || this.formData.trackB.husbandAlive === undefined) {
          return 'Please indicate if your husband is alive.';
        }
        if (!this.formData.trackB.title) {
          return "Please enter your husband's salutation.";
        }
        if (!this.formData.trackB.husbandFirstName) {
          return "Please enter your husband's first name.";
        }
        if (!this.formData.trackB.husbandSurname) {
          return "Please enter your husband's surname name.";
        }
        if (!this.formData.trackB.husbandDob) {
          return "Please enter your husband's date of birth.";
        }
        if (!this.formData.trackB.marriageDate) {
          return 'Please enter your marriage date.';
        }
        if (this.formData.trackB.sameAddress === null || this.formData.trackB.sameAddress === undefined) {
          return 'Please indicate if your husband lives at the same address.';
        }

        // Check address verification
        if (!this.formData.trackB.sameAddress) {
          if (!this.formData.trackB.country) {
            return "Please select your husband's country.";
          }
          if (!this.formData.trackB.postcode) {
            return "Please enter your husband's postcode.";
          }
          if (!this.formData.trackB.address1) {
            return "Please enter your husband's address.";
          }
          if (!this.formData.trackB.city) {
            return "Please enter your husband's city.";
          }
          if (this.formData.trackB.addressValidated !== true) {
            return 'Please verify your husband\'s address using the "Verify Address" button.';
          }
        } else if (this.formData.client.addressValidated !== true) {
          return 'Please verify your address using the "Verify Address" button.';
        }

        if (!this.formData.trackB.husbandHasNino) {
          return 'Please indicate if your husband has his National Insurance Number.';
        }
        if (this.formData.trackB.husbandHasNino === 'Yes' && !this.formData.trackB.husbandNino) {
          return "Please enter your husband's National Insurance Number.";
        }

        // Validate trace data if NINO is Forgot or Widow
        if (this.formData.trackB.husbandHasNino === 'Forgot' || this.formData.trackB.husbandAlive === false) {
          if (this.formData.trackB.employers.length === 0) {
            return 'Please add at least one UK employer for your husband.';
          }
          if (this.formData.trackB.addresses.length === 0) {
            return 'Please add at least one past UK address for your husband.';
          }

          for (let i = 0; i < this.formData.trackB.employers.length; i++) {
            const emp = this.formData.trackB.employers[i];
            if (!emp.name) {
              return `Please enter employer name for Husband's Employer ${i + 1}.`;
            }
            if (!emp.fromDate) {
              return `Please enter start date for Husband's Employer ${i + 1}.`;
            }
            if (!emp.toDate) {
              return `Please enter end date for Husband's Employer ${i + 1}.`;
            }
            if (emp.toDate < emp.fromDate) {
              return `End date must be after start date for Husband's Employer ${i + 1}.`;
            }
            if (!emp.postcode) {
              return `Please enter postcode for Husband's Employer ${i + 1}.`;
            }
            if (!emp.address1) {
              return `Please enter address for Husband's Employer ${i + 1}.`;
            }
            if (!emp.city) {
              return `Please enter city for Husband's Employer ${i + 1}.`;
            }
            // Uncomment if address verification is required
            // if (emp.addressValidated !== true) {
            //   return `Please verify address for Husband's Employer ${i + 1}.`;
            // }
          }
          for (let i = 0; i < this.formData.trackB.addresses.length; i++) {
            const addr = this.formData.trackB.addresses[i];
            if (!addr.postcode) {
              return `Please enter postcode for Husband's Past Address ${i + 1}.`;
            }
            if (!addr.address1) {
              return `Please enter address for Husband's Past Address ${i + 1}.`;
            }
            if (!addr.city) {
              return `Please enter city for Husband's Past Address ${i + 1}.`;
            }
            if (!addr.fromDate) {
              return `Please enter start date for Husband's Past Address ${i + 1}.`;
            }
            if (!addr.toDate) {
              return `Please enter end date for Husband's Past Address ${i + 1}.`;
            }
            if (addr.toDate < addr.fromDate) {
              return `End date must be after start date for Husband's Past Address ${i + 1}.`;
            }
            // Uncomment if address verification is required
            // if (addr.addressValidated !== true) {
            //   return `Please verify address for Husband's Past Address ${i + 1}.`;
            // }
          }
          if (this.formData.trackB.husbandAlive === false && !this.formData.trackB.townsLived) {
            return 'Please enter the towns/cities your husband lived in.';
          }
        }
        return 'Please complete all required fields for the Claim on Husband track.';
      }

      // Track C validation
      if (this.formData.track === 'C') {
        if (!this.formData.trackC.wifeDob) {
          return "Please enter your wife's date of birth.";
        }
        if (!this.formData.trackC.gatePassed) {
          return 'The 60% rule gate check failed. This case is not eligible.';
        }
        if (!this.formData.trackC.marriageDate) {
          return 'Please enter your marriage date.';
        }
        if (!this.formData.trackC.wifeLeftUK) {
          return 'Please enter the date your wife left the UK.';
        }
        if (this.formData.trackC.sameAddress === null || this.formData.trackC.sameAddress === undefined) {
          return 'Please indicate if your wife lives at the same address.';
        }

        // Check address verification
        if (!this.formData.trackC.sameAddress) {
          if (!this.formData.trackC.country) {
            return "Please select your wife's country.";
          }
          if (!this.formData.trackC.postcode) {
            return "Please enter your wife's postcode.";
          }
          if (!this.formData.trackC.address1) {
            return "Please enter your wife's address.";
          }
          if (!this.formData.trackC.city) {
            return "Please enter your wife's city.";
          }
          if (this.formData.trackC.addressValidated !== true) {
            return 'Please verify your wife\'s address using the "Verify Address" button.';
          }
        } else if (this.formData.client.addressValidated !== true) {
          return 'Please verify your address using the "Verify Address" button.';
        }

        if (!this.formData.trackC.wifeHasNino) {
          return 'Please indicate if your wife has her National Insurance Number.';
        }
        if (this.formData.trackC.wifeHasNino === 'Yes' && !this.formData.trackC.wifeNino) {
          return "Please enter your wife's National Insurance Number.";
        }

        // Validate trace data if NINO is Forgot
        if (this.formData.trackC.wifeHasNino === 'Forgot') {
          if (this.formData.trackC.employers.length === 0) {
            return 'Please add at least one UK employer for your wife.';
          }
          if (this.formData.trackC.addresses.length === 0) {
            return 'Please add at least one past UK address for your wife.';
          }

          for (let i = 0; i < this.formData.trackC.employers.length; i++) {
            const emp = this.formData.trackC.employers[i];
            if (!emp.name) {
              return `Please enter employer name for Wife's Employer ${i + 1}.`;
            }
            if (!emp.fromDate) {
              return `Please enter start date for Wife's Employer ${i + 1}.`;
            }
            if (!emp.toDate) {
              return `Please enter end date for Wife's Employer ${i + 1}.`;
            }
            if (emp.toDate < emp.fromDate) {
              return `End date must be after start date for Wife's Employer ${i + 1}.`;
            }
            if (!emp.postcode) {
              return `Please enter postcode for Wife's Employer ${i + 1}.`;
            }
            if (!emp.address1) {
              return `Please enter address for Wife's Employer ${i + 1}.`;
            }
            if (!emp.city) {
              return `Please enter city for Wife's Employer ${i + 1}.`;
            }
            // Uncomment if address verification is required
            // if (emp.addressValidated !== true) {
            //   return `Please verify address for Wife's Employer ${i + 1}.`;
            // }
          }
          for (let i = 0; i < this.formData.trackC.addresses.length; i++) {
            const addr = this.formData.trackC.addresses[i];
            if (!addr.postcode) {
              return `Please enter postcode for Wife's Past Address ${i + 1}.`;
            }
            if (!addr.address1) {
              return `Please enter address for Wife's Past Address ${i + 1}.`;
            }
            if (!addr.city) {
              return `Please enter city for Wife's Past Address ${i + 1}.`;
            }
            if (!addr.fromDate) {
              return `Please enter start date for Wife's Past Address ${i + 1}.`;
            }
            if (!addr.toDate) {
              return `Please enter end date for Wife's Past Address ${i + 1}.`;
            }
            if (addr.toDate < addr.fromDate) {
              return `End date must be after start date for Wife's Past Address ${i + 1}.`;
            }
            // Uncomment if address verification is required
            // if (addr.addressValidated !== true) {
            //   return `Please verify address for Wife's Past Address ${i + 1}.`;
            // }
          }
        }
        return 'Please complete all required fields for the Claim on Wife track.';
      }

      return 'Please complete all required fields for the selected track.';
    },

    initAutoSave() {
      const intervalTime = 60 * 1000;
      setInterval(() => {
        const autoSaveCondition =
          !this.isLoadingSave && !this.isLoadingInit && !this.isFormCompleted && this.isFormPending;

        if (autoSaveCondition) {
          this.autoSaveData();
        }
      }, intervalTime);
    },

    async autoSaveData() {
      try {
        await ApplicationService.updateApplicationShortDetails({
          applicationId: this.applicationId,
          application: this.formData,
          isAutoSave: true
        });
        this.showSnackbar({
          text: 'Form Auto Saved',
          color: 'success'
        });
      } catch (err) {
        console.error(err);
      }
    }
  }
};
</script>
<style scoped>
.step-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--n90);
}

.sec-hint {
  color: var(--n50);
  font-size: 13px;
  font-style: italic;
}

.track-card {
  cursor: pointer;
  transition: all 0.15s ease;
  border: 2px solid var(--n30);
}

.track-card:hover {
  border-color: #1976d2;
  background: #e3f2fd;
}

.track-selected {
  border-color: #7ab539 !important;
  background: #e8f5e9 !important;
}

.track-radio {
  display: flex;
  gap: 5px;
}

.track-hint {
  font-size: 11px;
  color: var(--n50);
  font-weight: 400;
  margin-top: 2px;
}

.rep-item {
  background: #fff;
  border: 1px solid var(--n10);
  border-radius: 4px;
  padding: 12px;
}

.rep-number {
  font-size: 11px;
  font-weight: 600;
  color: #5c9028;
  text-transform: uppercase;
}

.field-label {
  font-size: 12px;
  color: var(--n70);
  font-weight: 600;
}

.reassurance-box {
  background: #fffef3;
  border-left: 4px solid #fbc02d;
  border-radius: 0 6px 6px 0;
}

.form-header {
  display: flex;
  flex-direction: column;
}

.form-title {
  font-size: 24px;
  font-weight: 600;
  color: var(--n90);
}

.title-bar {
  width: 60px;
  height: 3px;
  background: #7ab539;
  margin-top: 4px;
}

.gap-2 {
  gap: 8px;
}

.address-validation-chip {
  font-size: 11px;
  font-weight: 600;
}

:deep(.v-chip--size-small) {
  font-size: 11px;
  height: 24px;
}

:deep(.v-chip--size-x-small) {
  font-size: 10px;
  height: 20px;
}

.text-caption {
  font-size: 12px;
}

/* Vuetify overrides */
:deep(.v-field--variant-outlined .v-field__input) {
  background: #fff;
}

:deep(.v-field--variant-outlined) {
  background: #fff;
}

:deep(.v-btn-toggle .v-btn) {
  text-transform: none;
  letter-spacing: 0.3px;
}

:deep(.v-btn-toggle .v-btn--variant-elevated) {
  box-shadow: none;
}

:deep(.v-btn--size-small) {
  font-size: 13px;
  padding: 6px 16px;
}
</style>
